import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Copy, ExternalLink, Plus, Edit, Trash2, Play, Pause, 
  CheckCircle, Clock, Users, FileText, Settings, Eye, Check, X,
  Loader2, Link as LinkIcon, Key, List, Code, AlertCircle, BarChart3, Upload,
  FileEdit, Monitor, MousePointer, Clipboard, Maximize, AlertTriangle, Camera,
  Shield, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Image as ImageIcon, Target, Globe, Lock, UserCheck,
  Trophy, Info, GripVertical, CheckSquare, Square, Zap
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getFrontendUrl } from '../utils/urlConfig';

const API = process.env.REACT_APP_BACKEND_URL;

// LaTeX Renderer Component
const LaTeXRenderer = ({ content }) => {
  if (!content) return null;
  
  const hasBlockMath = /\$\$[\s\S]+?\$\$/.test(content);
  const hasInlineMath = /\$[^$]+?\$/.test(content);
  
  if (!hasBlockMath && !hasInlineMath) {
    return <span>{content}</span>;
  }
  
  const parts = [];
  let key = 0;
  const blockRegex = /\$\$([\s\S]+?)\$\$/g;
  let lastIndex = 0;
  let match;
  
  while ((match = blockRegex.exec(content)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: 'text', content: content.slice(lastIndex, match.index), key: key++ });
    }
    parts.push({ type: 'block', content: match[1], key: key++ });
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < content.length) {
    parts.push({ type: 'text', content: content.slice(lastIndex), key: key++ });
  }
  
  const finalParts = [];
  parts.forEach(part => {
    if (part.type === 'block') {
      finalParts.push(part);
    } else {
      const inlineRegex = /\$([^$]+?)\$/g;
      let textLastIndex = 0;
      let textMatch;
      const text = part.content;
      
      while ((textMatch = inlineRegex.exec(text)) !== null) {
        if (textMatch.index > textLastIndex) {
          finalParts.push({ type: 'text', content: text.slice(textLastIndex, textMatch.index), key: key++ });
        }
        finalParts.push({ type: 'inline', content: textMatch[1], key: key++ });
        textLastIndex = textMatch.index + textMatch[0].length;
      }
      
      if (textLastIndex < text.length) {
        finalParts.push({ type: 'text', content: text.slice(textLastIndex), key: key++ });
      }
    }
  });
  
  return (
    <span>
      {finalParts.map(part => {
        if (part.type === 'block') {
          try {
            return <BlockMath key={part.key} math={part.content} />;
          } catch (e) {
            return <span key={part.key} className="text-red-500">[Math Error]</span>;
          }
        } else if (part.type === 'inline') {
          try {
            return <InlineMath key={part.key} math={part.content} />;
          } catch (e) {
            return <span key={part.key} className="text-red-500">[Math Error]</span>;
          }
        } else {
          return <span key={part.key}>{part.content}</span>;
        }
      })}
    </span>
  );
};

// Sortable Question Component for drag-and-drop
const SortableQuestion = ({ question, round, onUnassign, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-slate-50 rounded-lg border hover:bg-slate-100 transition-colors ${
        isDragging ? 'shadow-lg border-purple-300' : 'border-slate-200'
      }`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <button
          {...attributes}
          {...listeners}
          className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
          title="Drag to reorder"
        >
          <GripVertical size={16} />
        </button>
        <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
          question.type === 'mcq' ? 'bg-cyan-100 text-cyan-700' : 
          question.type === 'subjective' ? 'bg-amber-100 text-amber-700' :
          question.type === 'pdf' ? 'bg-red-100 text-red-700' :
          'bg-green-100 text-green-700'
        }`}>
          {question.type?.toUpperCase() || 'MCQ'}
        </span>
        <span className="text-sm text-gray-700 truncate">
          {question.title || question.problem_statement || question.pdf_file_name || 'Untitled'}
        </span>
        <span className="text-xs text-purple-600 flex-shrink-0">{question.points} pts</span>
        <span className="text-xs text-gray-400 flex-shrink-0">{question.difficulty || 'medium'}</span>
      </div>
      <div className="flex items-center gap-1 ml-2 flex-shrink-0">
        <button
          onClick={() => onUnassign(question.id, round.id)}
          className="p-1 text-amber-500 hover:bg-amber-50 rounded"
          title="Unassign from round"
        >
          <X size={14} />
        </button>
        <button
          onClick={() => onEdit(question)}
          className="p-1 text-gray-500 hover:bg-gray-100 rounded"
          title="Edit question"
        >
          <Edit size={14} />
        </button>
        <button
          onClick={() => onDelete(question.id)}
          className="p-1 text-red-500 hover:bg-red-50 rounded"
          title="Delete question"
        >
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
};

const CompanyEnterpriseDetailPage = () => {
  const { challengeId } = useParams();
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem('company_token'));
  
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [questions, setQuestions] = useState([]);
  
  // Modals
  const [showRoundModal, setShowRoundModal] = useState(false);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingRound, setEditingRound] = useState(null);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [expandedRound, setExpandedRound] = useState(null);
  const [roundAnalytics, setRoundAnalytics] = useState({});
  const [loadingAnalytics, setLoadingAnalytics] = useState(null);
  const [selectedAccessRound, setSelectedAccessRound] = useState(null);
  const [roundApprovedEmails, setRoundApprovedEmails] = useState({});
  const [roundNewEmail, setRoundNewEmail] = useState('');
  const [roundNewName, setRoundNewName] = useState('');
  const [roundBulkEmails, setRoundBulkEmails] = useState('');
  const [showRoundBulkInput, setShowRoundBulkInput] = useState(false);
  const [roundActiveTab, setRoundActiveTab] = useState({});  // {roundId: 'questions' | 'access'}
  const [roundInvites, setRoundInvites] = useState({});
  const [roundInviteEmail, setRoundInviteEmail] = useState('');
  const [loadingRoundAccess, setLoadingRoundAccess] = useState(null);
  
  // Bulk assignment state
  const [selectedUnassigned, setSelectedUnassigned] = useState(new Set());
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [bulkAssignTargetRound, setBulkAssignTargetRound] = useState('');
  const [bulkAssigning, setBulkAssigning] = useState(false);
  
  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  
  // Forms
  const [roundForm, setRoundForm] = useState({
    name: '', duration_minutes: 30, status: 'live', cutoff_marks: null, total_marks: null, access_type: 'public',
    // Navigation settings
    allow_navigation: true,  // Allow candidates to navigate between questions
    lock_answers_on_proceed: false,  // Lock answers when candidate moves to next question (only in sequential mode)
    // Advanced Assessment Mode
    advanced_mode: false,
    question_format: 'standard',  // standard, latex, pdf
    submission_type: 'standard',  // standard, pdf, image, mixed
    security_2fa: 'none',  // none, email_otp
    allow_pdf_submission: false,
    allow_image_submission: false,
    max_file_size_mb: 10
  });
  const [questionForm, setQuestionForm] = useState({
    type: 'mcq', 
    title: '', 
    options: ['', '', '', ''], 
    correct_answer: '', 
    points: 10,
    // Image support
    image_url: null,
    option_images: {},
    // Subjective fields
    word_limit: 500,
    expected_answer_hints: '',
    expected_answer: '',  // Model answer for evaluator reference
    // Coding fields
    problem_statement: '',
    input_format: '',
    output_format: '',
    constraints: '',
    sample_input: '',
    sample_output: '',
    difficulty: 'medium',
    test_cases: [{ input: '', expected_output: '', is_hidden: false }],
    // PDF question fields
    pdf_url: null,
    pdf_file_name: '',
    allow_pdf_answer: true,
    allow_image_answer: true
  });
  const [selectedRound, setSelectedRound] = useState(null);
  
  // Image upload state
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingOptionImage, setUploadingOptionImage] = useState(null);
  const [uploadingQuestionPdf, setUploadingQuestionPdf] = useState(false);
  const fileInputRef = useRef(null);
  const optionFileInputRef = useRef(null);
  const pdfFileInputRef = useRef(null);
  
  // Proctoring settings
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [proctoringSettings, setProctoringSettings] = useState({
    fullscreen_enforcement: true,
    tab_switch_detection: true,
    copy_paste_restriction: true,
    blur_detection: true,
    auto_warnings: true,
    auto_submit_on_violation: false,
    max_violations: 10,
    webcam_proctoring: false
  });
  
  // Result visibility settings
  const [resultSettings, setResultSettings] = useState({
    show_results_to_candidates: true,
    show_correct_answers: false,
    show_leaderboard: true
  });
  
  // Approved list
  const [approvedEmails, setApprovedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [bulkEmails, setBulkEmails] = useState('');
  const [showBulkInput, setShowBulkInput] = useState(false);
  const [bulkAddLoading, setBulkAddLoading] = useState(false);
  const [uploadingPdfQuestion, setUploadingPdfQuestion] = useState(null); // round id being uploaded

  // Upload PDF question paper for a round
  const uploadPdfQuestionPaper = async (roundId, file) => {
    if (!file || file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File size must be less than 20MB');
      return;
    }
    
    setUploadingPdfQuestion(roundId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/rounds/${roundId}/upload-question-paper`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success('Question paper uploaded successfully');
      fetchData(); // Refresh to show updated round
    } catch (err) {
      console.error('Upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload question paper');
    } finally {
      setUploadingPdfQuestion(null);
    }
  };

  useEffect(() => {
    if (!token) {
      navigate('/company/auth');
      return;
    }
    fetchData();
  }, [token, challengeId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const challengeRes = await axios.get(`${API}/api/company/enterprise/challenges/${challengeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Handle both wrapped and unwrapped response formats
      const challengeData = challengeRes.data.challenge || challengeRes.data;
      setChallenge(challengeData);
      setRounds(challengeData.rounds || []);
      
      // Load proctoring settings from challenge
      if (challengeData.proctoring_settings) {
        setProctoringSettings(prev => ({ ...prev, ...challengeData.proctoring_settings }));
      }
      
      // Load result visibility settings from challenge
      setResultSettings({
        show_results_to_candidates: challengeData.show_results_to_candidates !== false,
        show_correct_answers: challengeData.show_correct_answers || false,
        show_leaderboard: challengeData.show_leaderboard !== false
      });
      
      // Fetch questions for this company
      try {
        const questionsRes = await axios.get(`${API}/api/company/enterprise/questions?challenge_id=${challengeId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Handle both array and object responses
        const questionsData = questionsRes.data;
        setQuestions(Array.isArray(questionsData) ? questionsData : questionsData.questions || []);
      } catch (e) {
        console.log('Questions fetch failed, continuing without questions');
        setQuestions([]);
      }
      
      // Fetch approved list if pre_approved mode
      if (challengeData.access_mode === 'pre_approved') {
        try {
          const accessRes = await axios.get(
            `${API}/api/company/enterprise/challenges/${challengeId}/access-list`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setApprovedEmails(accessRes.data.approved_list || []);
        } catch (e) {}
      }
    } catch (err) {
      console.error('[ChallengeFetch] Error:', err);
      console.error('[ChallengeFetch] Response:', err.response?.data);
      
      // Show specific error message from backend
      let errorMessage = 'Failed to load challenge';
      if (err.response?.status === 403) {
        errorMessage = err.response?.data?.detail || 'Access denied: You may not have permission to view this challenge';
      } else if (err.response?.status === 404) {
        errorMessage = err.response?.data?.detail || 'Challenge not found';
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Copy to clipboard
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // Challenge Link
  const challengeLink = challenge 
    ? `${getFrontendUrl()}/enterprise/${challenge.org_slug || 'org'}/${challenge.slug}`
    : '';

  // Toggle challenge status
  const toggleStatus = async () => {
    try {
      const newStatus = challenge.status === 'live' ? 'draft' : 'live';
      await axios.put(
        `${API}/api/company/enterprise/challenges/${challengeId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChallenge(prev => ({ ...prev, status: newStatus }));
      toast.success(`Challenge is now ${newStatus === 'live' ? 'LIVE' : 'paused'}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Change access mode
  const changeAccessMode = async (mode) => {
    try {
      await axios.put(
        `${API}/api/company/enterprise/challenges/${challengeId}/access-mode`,
        { access_mode: mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChallenge(prev => ({ ...prev, access_mode: mode }));
      toast.success(`Access mode changed to ${mode}`);
    } catch (err) {
      toast.error('Failed to change access mode');
    }
  };

  // Add approved email
  const addApprovedEmail = async () => {
    if (!newEmail.trim()) return;
    try {
      await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/approved-list`,
        { entries: [{ email: newEmail.trim().toLowerCase(), name: newName.trim() || '' }] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setApprovedEmails(prev => [...prev, { email: newEmail.trim().toLowerCase(), name: newName.trim() || '' }]);
      setNewEmail('');
      setNewName('');
      toast.success('Email added to approved list');
    } catch (err) {
      toast.error('Failed to add email');
    }
  };

  // Bulk add approved emails
  const addBulkEmails = async () => {
    if (!bulkEmails.trim()) return;
    
    // Parse comma-separated emails
    const emails = bulkEmails
      .split(/[,\n;]+/)  // Split by comma, newline, or semicolon
      .map(e => e.trim().toLowerCase())
      .filter(e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));  // Basic email validation
    
    if (emails.length === 0) {
      toast.error('No valid emails found');
      return;
    }
    
    // Remove duplicates
    const uniqueEmails = [...new Set(emails)];
    const existingEmails = new Set(approvedEmails.map(a => a.email.toLowerCase()));
    const newEmails = uniqueEmails.filter(e => !existingEmails.has(e));
    
    if (newEmails.length === 0) {
      toast.error('All emails are already in the approved list');
      return;
    }
    
    try {
      setBulkAddLoading(true);
      const entries = newEmails.map(email => ({ email, name: '' }));
      
      await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/approved-list`,
        { entries },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setApprovedEmails(prev => [...prev, ...entries]);
      setBulkEmails('');
      setShowBulkInput(false);
      toast.success(`${newEmails.length} email${newEmails.length > 1 ? 's' : ''} added to approved list`);
    } catch (err) {
      toast.error('Failed to add emails');
    } finally {
      setBulkAddLoading(false);
    }
  };

  // Save proctoring settings
  const saveProctoringSettings = async () => {
    try {
      await axios.put(
        `${API}/api/company/enterprise/challenges/${challengeId}`,
        { 
          proctoring_settings: proctoringSettings,
          show_results_to_candidates: resultSettings.show_results_to_candidates,
          show_correct_answers: resultSettings.show_correct_answers,
          show_leaderboard: resultSettings.show_leaderboard
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setChallenge(prev => ({ 
        ...prev, 
        proctoring_settings: proctoringSettings,
        show_results_to_candidates: resultSettings.show_results_to_candidates,
        show_correct_answers: resultSettings.show_correct_answers,
        show_leaderboard: resultSettings.show_leaderboard
      }));
      setShowSettingsModal(false);
      toast.success('Settings saved');
    } catch (err) {
      toast.error('Failed to save settings');
    }
  };

  // Round CRUD
  // State for upgrade modal
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState(null);
  const [upgrading, setUpgrading] = useState(false);
  const [prorationPreview, setProrationPreview] = useState({});  // Store proration previews by plan ID
  const [loadingProration, setLoadingProration] = useState(false);

  // Fetch proration preview when modal opens
  const fetchProrationPreview = async (planId) => {
    if (prorationPreview[planId]) return; // Already fetched
    
    setLoadingProration(true);
    try {
      const res = await axios.get(
        `${API}/api/company/enterprise/challenges/${challengeId}/upgrade-preview?plan_id=${planId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProrationPreview(prev => ({ ...prev, [planId]: res.data }));
    } catch (err) {
      console.error('Failed to fetch proration preview:', err);
    } finally {
      setLoadingProration(false);
    }
  };

  // Fetch all proration previews when upgrade modal opens
  useEffect(() => {
    if (showUpgradeModal && upgradeInfo?.upgrade_options) {
      upgradeInfo.upgrade_options.forEach(plan => {
        fetchProrationPreview(plan.plan_id);
      });
    }
  }, [showUpgradeModal, upgradeInfo]);

  const saveRound = async () => {
    if (!roundForm.name.trim()) {
      toast.error('Please enter a round name');
      return;
    }
    
    try {
      const payload = {
        name: roundForm.name,
        duration_minutes: roundForm.duration_minutes || 30,
        status: roundForm.status || 'draft',
        cutoff_marks: roundForm.cutoff_marks ? parseInt(roundForm.cutoff_marks) : null,
        total_marks: roundForm.total_marks ? parseInt(roundForm.total_marks) : null,
        access_type: roundForm.access_type || 'public',
        // Navigation settings
        allow_navigation: roundForm.allow_navigation !== false,  // Default to true
        lock_answers_on_proceed: roundForm.lock_answers_on_proceed || false,  // Lock answers when proceeding (sequential mode only)
        // Advanced Assessment Mode fields
        advanced_mode: roundForm.advanced_mode || false,
        question_format: roundForm.question_format || 'standard',
        submission_type: roundForm.allow_pdf_submission && roundForm.allow_image_submission ? 'mixed' : 
                         roundForm.allow_pdf_submission ? 'pdf' : 
                         roundForm.allow_image_submission ? 'image' : 'standard',
        security_2fa: roundForm.security_2fa || 'none',
        allow_pdf_submission: roundForm.allow_pdf_submission || false,
        allow_image_submission: roundForm.allow_image_submission || false,
        max_file_size_mb: roundForm.max_file_size_mb || 10
      };
      
      if (editingRound) {
        await axios.put(
          `${API}/api/company/enterprise/challenges/${challengeId}/rounds/${editingRound.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Round updated');
      } else {
        await axios.post(
          `${API}/api/company/enterprise/challenges/${challengeId}/rounds`,
          { ...payload, round_number: rounds.length + 1 },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Round created');
      }
      setShowRoundModal(false);
      setEditingRound(null);
      setRoundForm({ name: '', duration_minutes: 30, status: 'live', cutoff_marks: null, total_marks: null, access_type: 'public', allow_navigation: true, lock_answers_on_proceed: false, advanced_mode: false, question_format: 'standard', submission_type: 'standard', security_2fa: 'none', allow_pdf_submission: false, allow_image_submission: false, max_file_size_mb: 10 });
      fetchData();
    } catch (err) {
      console.error('Save round error:', err);
      const errorData = err.response?.data;
      
      // Check if it's a round limit error with upgrade options
      if (errorData?.error_type === 'round_limit_exceeded' && errorData?.upgrade_required) {
        setUpgradeInfo(errorData);
        setShowUpgradeModal(true);
        setShowRoundModal(false);
      } else {
        toast.error(errorData?.detail || 'Failed to save round');
      }
    }
  };

  const handleUpgradePlan = async (planId) => {
    setUpgrading(true);
    try {
      const res = await axios.post(
        `${API}/api/company/enterprise/challenges/${challengeId}/upgrade-plan`,
        { plan_id: planId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data.payment_session_id) {
        const { load } = await import('@cashfreepayments/cashfree-js');
        const cashfree = await load({ mode: 'production' });
        await cashfree.checkout({
          paymentSessionId: res.data.payment_session_id,
          redirectTarget: '_self'
        });
      } else if (res.data.message?.includes('upgraded')) {
        // Free upgrade completed
        toast.success('Plan upgraded successfully!');
        setShowUpgradeModal(false);
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upgrade plan');
    } finally {
      setUpgrading(false);
    }
  };

  const deleteRound = async (roundId) => {
    if (!confirm('Delete this round?')) return;
    try {
      await axios.delete(
        `${API}/api/company/enterprise/challenges/${challengeId}/rounds/${roundId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Round deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete round');
    }
  };

  const toggleRoundStatus = async (round) => {
    try {
      const newStatus = round.status === 'live' ? 'draft' : 'live';
      await axios.put(
        `${API}/api/company/enterprise/challenges/${challengeId}/rounds/${round.id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Round is now ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error('Failed to update round status');
    }
  };

  // Question CRUD
  const saveQuestion = async () => {
    if (savingQuestion) return; // Prevent double submission
    
    // Validate PDF question has a PDF uploaded
    if (questionForm.type === 'pdf' && !questionForm.pdf_url) {
      toast.error('Please upload a PDF question paper before saving');
      return;
    }
    
    try {
      setSavingQuestion(true);
      
      // Build payload based on question type
      let payload = {
        type: questionForm.type,
        title: questionForm.title,
        points: questionForm.points,
        challenge_id: challengeId,
        round_id: selectedRound?.id,
        image_url: questionForm.image_url,
        option_images: questionForm.option_images || {}
      };
      
      if (questionForm.type === 'mcq') {
        payload.options = (questionForm.options || []).filter(o => o && o.trim());
        payload.correct_answer = questionForm.correct_answer;
      } else if (questionForm.type === 'subjective') {
        payload.word_limit = questionForm.word_limit;
        payload.expected_answer_hints = questionForm.expected_answer_hints;
        payload.expected_answer = questionForm.expected_answer;  // Model answer for evaluator reference
      } else if (questionForm.type === 'pdf') {
        payload.pdf_url = questionForm.pdf_url;
        payload.pdf_file_name = questionForm.pdf_file_name;
        payload.allow_pdf_answer = questionForm.allow_pdf_answer;
        payload.allow_image_answer = questionForm.allow_image_answer;
        // Title field is used for instructions in PDF type
        payload.instructions = questionForm.title;
      } else if (questionForm.type === 'coding') {
        payload.problem_statement = questionForm.problem_statement || questionForm.title;
        payload.input_format = questionForm.input_format || '';
        payload.output_format = questionForm.output_format || '';
        payload.constraints = questionForm.constraints || '';
        payload.sample_input = questionForm.sample_input || '';
        payload.sample_output = questionForm.sample_output || '';
        payload.difficulty = questionForm.difficulty || 'medium';
        // Safely filter test cases
        const testCases = questionForm.test_cases || [];
        payload.test_cases = testCases.filter(tc => tc && ((tc.input && tc.input.trim()) || (tc.expected_output && tc.expected_output.trim())));
      }
      
      if (editingQuestion) {
        await axios.put(
          `${API}/api/company/enterprise/questions/${editingQuestion.id}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Question updated');
      } else {
        const res = await axios.post(
          `${API}/api/company/enterprise/challenges/${challengeId}/questions`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Add to round if selected
        if (selectedRound && res.data.id) {
          try {
            await axios.post(
              `${API}/api/company/enterprise/rounds/${selectedRound.id}/questions`,
              { question_ids: [res.data.id] },
              { headers: { Authorization: `Bearer ${token}` } }
            );
          } catch (linkErr) {
            console.error('Failed to link question to round:', linkErr);
          }
        }
        toast.success('Question created');
      }
      setShowQuestionModal(false);
      setEditingQuestion(null);
      resetQuestionForm();
      fetchData();
    } catch (err) {
      console.error('Save question error:', err);
      toast.error(err.response?.data?.detail || 'Failed to save question');
    } finally {
      setSavingQuestion(false);
    }
  };
  
  // Get questions for a specific round
  const getQuestionsForRound = (roundId) => {
    const round = rounds.find(r => r.id === roundId);
    if (!round || !round.question_ids) return [];
    return questions.filter(q => round.question_ids.includes(q.id));
  };
  
  // Get unassigned questions
  const getUnassignedQuestions = () => {
    const assignedIds = new Set(rounds.flatMap(r => r.question_ids || []));
    return questions.filter(q => !assignedIds.has(q.id));
  };

  // Assign question to a round
  const assignQuestionToRound = async (questionId, roundId) => {
    try {
      await axios.post(
        `${API}/api/company/enterprise/rounds/${roundId}/questions`,
        { question_ids: [questionId] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state - add question to round
      setRounds(prev => prev.map(r => {
        if (r.id === roundId) {
          return {
            ...r,
            question_ids: [...(r.question_ids || []), questionId]
          };
        }
        return r;
      }));
      
      toast.success('Question assigned to round');
    } catch (err) {
      console.error('Failed to assign question:', err);
      toast.error(err.response?.data?.detail || 'Failed to assign question');
    }
  };

  // Unassign question from a round
  const unassignQuestionFromRound = async (questionId, roundId) => {
    try {
      await axios.delete(
        `${API}/api/company/enterprise/rounds/${roundId}/questions/${questionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state - remove question from round
      setRounds(prev => prev.map(r => {
        if (r.id === roundId) {
          return {
            ...r,
            question_ids: (r.question_ids || []).filter(qId => qId !== questionId)
          };
        }
        return r;
      }));
      
      toast.success('Question removed from round');
    } catch (err) {
      console.error('Failed to unassign question:', err);
      toast.error('Failed to remove question from round');
    }
  };

  // Bulk assign questions to a round
  const bulkAssignQuestions = async () => {
    if (!bulkAssignTargetRound || selectedUnassigned.size === 0) return;
    
    setBulkAssigning(true);
    try {
      await axios.post(
        `${API}/api/company/enterprise/rounds/${bulkAssignTargetRound}/questions`,
        { question_ids: Array.from(selectedUnassigned) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update local state
      setRounds(prev => prev.map(r => {
        if (r.id === bulkAssignTargetRound) {
          return {
            ...r,
            question_ids: [...(r.question_ids || []), ...Array.from(selectedUnassigned)]
          };
        }
        return r;
      }));
      
      toast.success(`${selectedUnassigned.size} questions assigned to round`);
      setSelectedUnassigned(new Set());
      setShowBulkAssignModal(false);
      setBulkAssignTargetRound('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign questions');
    } finally {
      setBulkAssigning(false);
    }
  };

  // Toggle selection of unassigned question
  const toggleUnassignedSelection = (questionId) => {
    setSelectedUnassigned(prev => {
      const newSet = new Set(prev);
      if (newSet.has(questionId)) {
        newSet.delete(questionId);
      } else {
        newSet.add(questionId);
      }
      return newSet;
    });
  };

  // Select all unassigned questions
  const selectAllUnassigned = () => {
    const unassigned = getUnassignedQuestions();
    setSelectedUnassigned(new Set(unassigned.map(q => q.id)));
  };

  // Deselect all
  const deselectAllUnassigned = () => {
    setSelectedUnassigned(new Set());
  };

  // Handle drag end for reordering questions within a round
  const handleDragEnd = async (event, roundId) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const round = rounds.find(r => r.id === roundId);
    if (!round) return;
    
    const questionIds = round.question_ids || [];
    const oldIndex = questionIds.indexOf(active.id);
    const newIndex = questionIds.indexOf(over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newOrder = arrayMove(questionIds, oldIndex, newIndex);
    
    // Optimistically update local state
    setRounds(prev => prev.map(r => {
      if (r.id === roundId) {
        return { ...r, question_ids: newOrder };
      }
      return r;
    }));
    
    // Save to backend
    try {
      await axios.put(
        `${API}/api/company/enterprise/rounds/${roundId}/question-order`,
        { question_ids: newOrder },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Question order updated');
    } catch (err) {
      // Revert on error
      setRounds(prev => prev.map(r => {
        if (r.id === roundId) {
          return { ...r, question_ids: questionIds };
        }
        return r;
      }));
      toast.error('Failed to update question order');
    }
  };

  // Fetch round analytics
  const fetchRoundAnalytics = async (roundId) => {
    try {
      setLoadingAnalytics(roundId);
      const res = await axios.get(
        `${API}/api/company/enterprise/challenges/${challengeId}/rounds/${roundId}/analytics`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoundAnalytics(prev => ({ ...prev, [roundId]: res.data }));
    } catch (err) {
      console.error('Failed to fetch round analytics:', err);
    } finally {
      setLoadingAnalytics(null);
    }
  };

  // Copy round link
  const copyRoundLink = (round, idx) => {
    const baseUrl = getFrontendUrl();
    const roundLink = `${baseUrl}/enterprise/${challenge.org_slug || 'org'}/${challenge.slug}?round=${round.id}`;
    navigator.clipboard.writeText(roundLink);
    toast.success(`Round ${idx + 1} link copied!`);
  };

  // Get round status display
  const getRoundStatusInfo = (round, idx) => {
    const prevRound = idx > 0 ? rounds[idx - 1] : null;
    const hasQuestions = (round.question_ids?.length || 0) > 0;
    
    if (round.status === 'completed') {
      return { status: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-700' };
    }
    if (round.status === 'live') {
      return { status: 'live', label: 'Live', color: 'bg-green-100 text-green-700' };
    }
    // Check if previous round is completed (for sequential rounds)
    if (prevRound && prevRound.status !== 'completed') {
      return { status: 'waiting', label: 'Waiting', color: 'bg-gray-100 text-gray-500' };
    }
    if (!hasQuestions) {
      return { status: 'draft', label: 'No Questions', color: 'bg-amber-100 text-amber-700' };
    }
    return { status: 'draft', label: 'Draft', color: 'bg-gray-200 text-gray-600' };
  };

  // Complete round and apply cutoff
  const completeRound = async (round) => {
    try {
      await axios.put(
        `${API}/api/company/enterprise/challenges/${challengeId}/rounds/${round.id}`,
        { status: 'completed' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Round marked as completed');
      fetchData();
    } catch (err) {
      toast.error('Failed to complete round');
    }
  };

  // Get access type display info
  const getAccessTypeInfo = (accessType) => {
    switch (accessType) {
      case 'private':
        return { icon: Lock, label: 'Private', color: 'text-red-600 bg-red-50' };
      case 'invite':
      case 'invite_only':
        return { icon: UserCheck, label: 'Invite Only', color: 'text-amber-600 bg-amber-50' };
      case 'pre_approved':
        return { icon: Users, label: 'Pre-Approved', color: 'text-blue-600 bg-blue-50' };
      default:
        return { icon: Globe, label: 'Public', color: 'text-green-600 bg-green-50' };
    }
  };

  // Change round access type
  const changeRoundAccessType = async (roundId, accessType) => {
    try {
      await axios.put(
        `${API}/api/company/enterprise/challenges/${challengeId}/rounds/${roundId}`,
        { access_type: accessType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Access type updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update access type');
    }
  };

  // Fetch round approved list
  const fetchRoundApprovedList = async (roundId) => {
    try {
      setLoadingRoundAccess(roundId);
      const res = await axios.get(
        `${API}/api/company/enterprise/rounds/${roundId}/approved-list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoundApprovedEmails(prev => ({ ...prev, [roundId]: res.data.approved_list || [] }));
    } catch (err) {
      // If no approved list exists yet, set empty array
      setRoundApprovedEmails(prev => ({ ...prev, [roundId]: [] }));
    } finally {
      setLoadingRoundAccess(null);
    }
  };

  // Fetch round invites
  const fetchRoundInvites = async (roundId) => {
    try {
      setLoadingRoundAccess(roundId);
      const res = await axios.get(
        `${API}/api/company/enterprise/rounds/${roundId}/invites`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoundInvites(prev => ({ ...prev, [roundId]: res.data.invites || [] }));
    } catch (err) {
      setRoundInvites(prev => ({ ...prev, [roundId]: [] }));
    } finally {
      setLoadingRoundAccess(null);
    }
  };

  // Add email to round approved list
  const addRoundApprovedEmail = async (roundId) => {
    if (!roundNewEmail.trim()) return;
    try {
      await axios.post(
        `${API}/api/company/enterprise/rounds/${roundId}/approved-list`,
        { entries: [{ email: roundNewEmail.trim().toLowerCase(), name: roundNewName.trim() || '' }] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoundApprovedEmails(prev => ({
        ...prev,
        [roundId]: [...(prev[roundId] || []), { email: roundNewEmail.trim().toLowerCase(), name: roundNewName.trim() || '' }]
      }));
      setRoundNewEmail('');
      setRoundNewName('');
      toast.success('Email added');
    } catch (err) {
      toast.error('Failed to add email');
    }
  };

  // Bulk add emails to round
  const addRoundBulkEmails = async (roundId) => {
    if (!roundBulkEmails.trim()) return;
    
    const emails = roundBulkEmails
      .split(/[,\n;]+/)
      .map(e => e.trim().toLowerCase())
      .filter(e => e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
    
    if (emails.length === 0) {
      toast.error('No valid emails found');
      return;
    }
    
    const uniqueEmails = [...new Set(emails)];
    const existingEmails = new Set((roundApprovedEmails[roundId] || []).map(a => a.email.toLowerCase()));
    const newEmails = uniqueEmails.filter(e => !existingEmails.has(e));
    
    if (newEmails.length === 0) {
      toast.error('All emails are already in the approved list');
      return;
    }
    
    try {
      const entries = newEmails.map(email => ({ email, name: '' }));
      await axios.post(
        `${API}/api/company/enterprise/rounds/${roundId}/approved-list`,
        { entries },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoundApprovedEmails(prev => ({
        ...prev,
        [roundId]: [...(prev[roundId] || []), ...entries]
      }));
      setRoundBulkEmails('');
      setShowRoundBulkInput(false);
      toast.success(`${newEmails.length} email${newEmails.length > 1 ? 's' : ''} added`);
    } catch (err) {
      toast.error('Failed to add emails');
    }
  };

  // Delete round approved entry
  const deleteRoundApprovedEntry = async (roundId, entryId) => {
    try {
      await axios.delete(
        `${API}/api/company/enterprise/rounds/${roundId}/approved-list/${entryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoundApprovedEmails(prev => ({
        ...prev,
        [roundId]: (prev[roundId] || []).filter(e => e.id !== entryId)
      }));
      toast.success('Entry removed');
    } catch (err) {
      toast.error('Failed to remove entry');
    }
  };

  // Create round invite
  const createRoundInvite = async (roundId) => {
    if (!roundInviteEmail.trim()) return;
    try {
      const res = await axios.post(
        `${API}/api/company/enterprise/rounds/${roundId}/invites`,
        { emails: [roundInviteEmail.trim().toLowerCase()] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.invites && res.data.invites.length > 0) {
        setRoundInvites(prev => ({
          ...prev,
          [roundId]: [...(prev[roundId] || []), ...res.data.invites.map(inv => ({
            ...inv,
            status: 'pending',
            created_at: new Date().toISOString()
          }))]
        }));
        toast.success('Invite created');
      }
      if (res.data.errors && res.data.errors.length > 0) {
        toast.error(res.data.errors[0].reason);
      }
      setRoundInviteEmail('');
    } catch (err) {
      toast.error('Failed to create invite');
    }
  };

  // Delete round invite
  const deleteRoundInvite = async (roundId, inviteId) => {
    try {
      await axios.delete(
        `${API}/api/company/enterprise/rounds/${roundId}/invites/${inviteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRoundInvites(prev => ({
        ...prev,
        [roundId]: (prev[roundId] || []).filter(i => i.id !== inviteId)
      }));
      toast.success('Invite revoked');
    } catch (err) {
      toast.error('Failed to revoke invite');
    }
  };

  // Copy round invite link
  const copyRoundInviteLink = (round, invite) => {
    const baseUrl = getFrontendUrl();
    const link = `${baseUrl}/enterprise/${challenge?.org_slug || 'company'}/${challenge?.slug || challengeId}?round=${round.id}&invite=${invite.invite_token || invite.token}`;
    navigator.clipboard.writeText(link);
    toast.success('Invite link copied!');
  };
  
  const resetQuestionForm = () => {
    setQuestionForm({
      type: 'mcq', 
      title: '', 
      options: ['', '', '', ''], 
      correct_answer: '', 
      points: 10,
      image_url: null,
      option_images: {},
      word_limit: 500,
      expected_answer_hints: '',
      expected_answer: '',  // Model answer for evaluator reference
      problem_statement: '',
      input_format: '',
      output_format: '',
      constraints: '',
      sample_input: '',
      sample_output: '',
      difficulty: 'medium',
      test_cases: [{ input: '', expected_output: '', is_hidden: false }]
    });
  };
  
  // Image upload handlers
  const handleImageUpload = async (file, isOptionImage = false, optionIndex = null) => {
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      if (isOptionImage) {
        setUploadingOptionImage(optionIndex);
      } else {
        setUploadingImage(true);
      }
      
      const res = await axios.post(
        `${API}/api/company/enterprise/upload-image`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      if (res.data.success) {
        if (isOptionImage && optionIndex !== null) {
          setQuestionForm(prev => ({
            ...prev,
            option_images: { ...prev.option_images, [optionIndex]: res.data.file_id }
          }));
        } else {
          setQuestionForm(prev => ({ ...prev, image_url: res.data.file_id }));
        }
        toast.success('Image uploaded');
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload image');
    } finally {
      setUploadingImage(false);
      setUploadingOptionImage(null);
    }
  };
  
  const removeImage = (isOptionImage = false, optionIndex = null) => {
    if (isOptionImage && optionIndex !== null) {
      setQuestionForm(prev => {
        const newOptionImages = { ...prev.option_images };
        delete newOptionImages[optionIndex];
        return { ...prev, option_images: newOptionImages };
      });
    } else {
      setQuestionForm(prev => ({ ...prev, image_url: null }));
    }
  };

  // Upload PDF for PDF question type
  const handlePdfQuestionUpload = async (file) => {
    if (!file || file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }
    
    if (file.size > 20 * 1024 * 1024) {
      toast.error('File size must be less than 20MB');
      return;
    }
    
    setUploadingQuestionPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(
        `${API}/api/company/enterprise/upload-pdf-question`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      if (res.data.success) {
        setQuestionForm(prev => ({
          ...prev,
          pdf_url: res.data.file_url,
          pdf_file_name: file.name
        }));
        toast.success('PDF uploaded successfully');
      }
    } catch (err) {
      console.error('PDF upload error:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload PDF');
    } finally {
      setUploadingQuestionPdf(false);
    }
  };

  const removePdfQuestion = () => {
    setQuestionForm(prev => ({ ...prev, pdf_url: null, pdf_file_name: '' }));
  };
  
  const addTestCase = () => {
    setQuestionForm(prev => ({
      ...prev,
      test_cases: [...(prev.test_cases || []), { input: '', expected_output: '', is_hidden: false }]
    }));
  };
  
  const removeTestCase = (idx) => {
    setQuestionForm(prev => ({
      ...prev,
      test_cases: (prev.test_cases || []).filter((_, i) => i !== idx)
    }));
  };
  
  const updateTestCase = (idx, field, value) => {
    setQuestionForm(prev => ({
      ...prev,
      test_cases: (prev.test_cases || []).map((tc, i) => 
        i === idx ? { ...tc, [field]: value } : tc
      )
    }));
  };

  const deleteQuestion = async (questionId) => {
    if (!confirm('Delete this question?')) return;
    try {
      await axios.delete(
        `${API}/api/company/enterprise/questions/${questionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Question deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete question');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-600" size={48} />
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Challenge not found</h2>
          <p className="text-gray-600 mb-4">
            This could happen if:
            <br />• The challenge was deleted
            <br />• You're logged in with a different company account
            <br />• The URL is incorrect
          </p>
          <button
            onClick={() => navigate('/company/enterprise')}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Go to My Challenges
          </button>
        </div>
      </div>
    );
  }

  const liveRounds = rounds.filter(r => r.status === 'live');

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/company/enterprise')} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{challenge.title}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    challenge.status === 'live' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {challenge.status === 'live' ? 'LIVE' : 'DRAFT'}
                  </span>
                  <span className="text-sm text-gray-500">
                    {liveRounds.length} active round{liveRounds.length !== 1 ? 's' : ''} • {questions.length} questions
                  </span>
                </div>
              </div>
            </div>
            
            {/* Status Toggle */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSettingsModal(true)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 flex items-center gap-2"
              >
                <Settings size={16} /> Settings
              </button>
              <button
                onClick={() => navigate(`/company/enterprise/challenges/${challengeId}/analytics`)}
                className="px-4 py-2 bg-purple-100 text-purple-700 rounded-lg font-medium hover:bg-purple-200 flex items-center gap-2"
              >
                <BarChart3 size={16} /> Analytics
              </button>
              <button
                onClick={toggleStatus}
                className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
                  challenge.status === 'live'
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {challenge.status === 'live' ? <><Pause size={16} /> Pause</> : <><Play size={16} /> Go Live</>}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Left Column - Quick Info */}
          <div className="space-y-4">
            {/* Challenge Link */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <LinkIcon size={16} className="text-purple-600" />
                Challenge Link
              </h3>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={challengeLink}
                  readOnly
                  className="flex-1 px-3 py-2 bg-gray-50 border rounded-lg text-sm text-gray-600"
                />
                <button
                  onClick={() => copyToClipboard(challengeLink, 'Link')}
                  className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200"
                  title="Copy link"
                >
                  <Copy size={16} />
                </button>
                <a
                  href={challengeLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                  title="Open link"
                >
                  <ExternalLink size={16} />
                </a>
              </div>
            </div>

            {/* Access Mode */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Key size={16} className="text-purple-600" />
                Access Control
              </h3>
              <div className="space-y-2">
                {['public', 'invite_only', 'pre_approved'].map(mode => (
                  <label key={mode} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="radio"
                      name="access_mode"
                      checked={challenge.access_mode === mode}
                      onChange={() => changeAccessMode(mode)}
                      className="w-4 h-4 text-purple-600"
                    />
                    <span className="text-sm text-gray-700 capitalize">{mode.replace('_', ' ')}</span>
                  </label>
                ))}
              </div>
              
              {/* Pre-approved email list */}
              {challenge.access_mode === 'pre_approved' && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">Approved Candidates</p>
                    <button
                      onClick={() => setShowBulkInput(!showBulkInput)}
                      className="text-xs text-purple-600 hover:text-purple-700 flex items-center gap-1"
                    >
                      {showBulkInput ? 'Single Add' : 'Bulk Add'}
                    </button>
                  </div>
                  
                  {/* Single Add Mode */}
                  {!showBulkInput && (
                    <div className="space-y-2 mb-2">
                      <input
                        type="text"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="Candidate Name"
                        className="w-full px-3 py-2 border rounded-lg text-sm"
                      />
                      <div className="flex gap-2">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="email@example.com"
                          className="flex-1 px-3 py-2 border rounded-lg text-sm"
                        />
                        <button
                          onClick={addApprovedEmail}
                          className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Bulk Add Mode */}
                  {showBulkInput && (
                    <div className="space-y-2 mb-2">
                      <textarea
                        value={bulkEmails}
                        onChange={(e) => setBulkEmails(e.target.value)}
                        placeholder="Paste comma-separated emails:&#10;user1@gmail.com, user2@gmail.com, user3@gmail.com"
                        className="w-full px-3 py-2 border rounded-lg text-sm h-24 font-mono resize-none"
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500">
                          {bulkEmails.trim() ? (
                            <>
                              {bulkEmails.split(/[,\n;]+/).filter(e => e.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())).length} valid emails detected
                            </>
                          ) : (
                            'Separate emails with commas, newlines, or semicolons'
                          )}
                        </p>
                        <button
                          onClick={addBulkEmails}
                          disabled={bulkAddLoading || !bulkEmails.trim()}
                          className="px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                          {bulkAddLoading ? (
                            <><Loader2 size={14} className="animate-spin" /> Adding...</>
                          ) : (
                            'Add All'
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Approved List Display */}
                  <div className="max-h-32 overflow-y-auto space-y-1">
                    {approvedEmails.length === 0 ? (
                      <p className="text-xs text-gray-400 text-center py-2">No candidates added yet</p>
                    ) : (
                      <>
                        {approvedEmails.slice(0, 10).map((item, idx) => (
                          <div key={idx} className="text-xs text-gray-600 bg-gray-50 px-2 py-1 rounded flex items-center justify-between">
                            <div>
                              <span className="font-medium">{item.name || 'No name'}</span>
                              <span className="ml-2 text-gray-400">{item.email}</span>
                            </div>
                          </div>
                        ))}
                        {approvedEmails.length > 10 && (
                          <p className="text-xs text-gray-500">+{approvedEmails.length - 10} more</p>
                        )}
                      </>
                    )}
                  </div>
                  
                  {approvedEmails.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2 text-center">
                      Total: {approvedEmails.length} candidate{approvedEmails.length !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Eye size={16} className="text-purple-600" />
                Preview Assessment
              </h3>
              <a
                href={challenge.preview_url || `/enterprise/${challenge.org_slug || 'org'}/${challenge.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 text-sm"
              >
                <Eye size={14} /> View as Candidate
              </a>
              <p className="text-xs text-gray-500 mt-2 text-center">
                Preview link expires in 1 hour
              </p>
            </div>
          </div>

          {/* Middle Column - Rounds with Questions */}
          <div className="lg:col-span-2 space-y-4">
            {/* Rounds Header */}
            <div className="bg-white rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Clock size={16} className="text-purple-600" />
                  Rounds & Questions
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingQuestion(null);
                      resetQuestionForm();
                      setShowQuestionModal(true);
                    }}
                    className="px-3 py-1.5 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 text-sm flex items-center gap-1"
                  >
                    <Plus size={14} /> Add Question
                  </button>
                  <button
                    onClick={() => {
                      setEditingRound(null);
                      setRoundForm({ name: '', duration_minutes: 30, status: 'live', cutoff_marks: null, total_marks: null });
                      setShowRoundModal(true);
                    }}
                    className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 text-sm flex items-center gap-1"
                  >
                    <Plus size={14} /> Add Round
                  </button>
                </div>
              </div>
              
              {rounds.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="mx-auto mb-2 text-gray-300" size={32} />
                  <p className="text-sm">No rounds yet</p>
                  <button
                    onClick={() => setShowRoundModal(true)}
                    className="mt-2 text-purple-600 text-sm hover:underline"
                  >
                    Create first round
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {rounds.map((round, idx) => {
                    const roundQuestions = getQuestionsForRound(round.id);
                    const isExpanded = expandedRound === round.id;
                    const totalMarks = roundQuestions.reduce((sum, q) => sum + (q.points || 0), 0);
                    const statusInfo = getRoundStatusInfo(round, idx);
                    const analytics = roundAnalytics[round.id];
                    const prevRound = idx > 0 ? rounds[idx - 1] : null;
                    const canGoLive = (!prevRound || prevRound.status === 'completed') && roundQuestions.length > 0;
                    
                    return (
                      <div key={round.id} className="border rounded-lg overflow-hidden">
                        {/* Round Header */}
                        <div className="p-4 bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-3">
                              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                round.status === 'completed' ? 'bg-blue-100 text-blue-600' :
                                round.status === 'live' ? 'bg-green-100 text-green-600' :
                                'bg-purple-100 text-purple-600'
                              }`}>
                                {idx + 1}
                              </span>
                              <div>
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-gray-900">{round.name}</span>
                                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusInfo.color}`}>
                                    {statusInfo.label}
                                  </span>
                                  {/* Access Type Badge */}
                                  {(() => {
                                    const accessInfo = getAccessTypeInfo(round.access_type);
                                    const AccessIcon = accessInfo.icon;
                                    return (
                                      <span className={`px-2 py-0.5 rounded text-xs flex items-center gap-1 ${accessInfo.color}`}>
                                        <AccessIcon size={10} /> {accessInfo.label}
                                      </span>
                                    );
                                  })()}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-gray-500 mt-1">
                                  <span className="flex items-center gap-1"><Clock size={10} /> {round.duration_minutes} min</span>
                                  <span>•</span>
                                  <span>{roundQuestions.length} questions</span>
                                  <span>•</span>
                                  <span>{totalMarks} marks</span>
                                  {round.cutoff_marks && (
                                    <>
                                      <span>•</span>
                                      <span className="text-purple-600 font-medium">Cutoff: {round.cutoff_marks}</span>
                                    </>
                                  )}
                                </div>
                                {/* Advanced Mode Badges */}
                                {round.advanced_mode && (
                                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                                    <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium flex items-center gap-1">
                                      <Shield size={10} /> Advanced Mode
                                    </span>
                                    {round.question_format === 'pdf' && (
                                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs font-medium flex items-center gap-1">
                                        <FileText size={10} /> PDF Questions
                                      </span>
                                    )}
                                    {round.allow_pdf_submission && (
                                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                                        <Upload size={10} /> PDF Submission
                                      </span>
                                    )}
                                    {round.allow_image_submission && (
                                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium flex items-center gap-1">
                                        <Camera size={10} /> Image Submission
                                      </span>
                                    )}
                                    {round.security_2fa === 'email_otp' && (
                                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium flex items-center gap-1">
                                        <Key size={10} /> Email OTP
                                      </span>
                                    )}
                                    {round.question_paper_url && (
                                      <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium flex items-center gap-1">
                                        <CheckCircle size={10} /> Paper Uploaded
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            {/* Round Actions */}
                            <div className="flex items-center gap-2">
                              {/* Share Link Button */}
                              {round.status === 'live' && (
                                <button
                                  onClick={() => copyRoundLink(round, idx)}
                                  className="px-2 py-1 text-xs bg-blue-50 text-blue-600 rounded flex items-center gap-1 hover:bg-blue-100"
                                  title="Copy round link"
                                >
                                  <Copy size={12} /> Share Link
                                </button>
                              )}
                              
                              {/* Status Actions */}
                              {round.status === 'draft' && canGoLive && (
                                <button
                                  onClick={() => toggleRoundStatus(round)}
                                  className="px-3 py-1 text-xs bg-green-500 text-white rounded flex items-center gap-1 hover:bg-green-600"
                                >
                                  <Play size={12} /> Go Live
                                </button>
                              )}
                              {round.status === 'live' && (
                                <>
                                  <button
                                    onClick={() => toggleRoundStatus(round)}
                                    className="px-2 py-1 text-xs bg-amber-100 text-amber-700 rounded flex items-center gap-1 hover:bg-amber-200"
                                  >
                                    <Pause size={12} /> Pause
                                  </button>
                                  <button
                                    onClick={() => completeRound(round)}
                                    className="px-2 py-1 text-xs bg-blue-500 text-white rounded flex items-center gap-1 hover:bg-blue-600"
                                    title="Complete round and apply cutoff"
                                  >
                                    <CheckCircle size={12} /> Complete
                                  </button>
                                </>
                              )}
                              
                              {/* PDF Question Paper Upload for Advanced Mode */}
                              {round.advanced_mode && round.question_format === 'pdf' && !round.question_paper_url && (
                                <div className="relative">
                                  <input
                                    type="file"
                                    accept=".pdf"
                                    id={`pdf-question-upload-${round.id}`}
                                    className="hidden"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) uploadPdfQuestionPaper(round.id, file);
                                    }}
                                  />
                                  <label
                                    htmlFor={`pdf-question-upload-${round.id}`}
                                    className={`px-2 py-1 text-xs bg-red-500 text-white rounded flex items-center gap-1 hover:bg-red-600 cursor-pointer ${
                                      uploadingPdfQuestion === round.id ? 'opacity-50 pointer-events-none' : ''
                                    }`}
                                  >
                                    {uploadingPdfQuestion === round.id ? (
                                      <><Loader2 size={12} className="animate-spin" /> Uploading...</>
                                    ) : (
                                      <><Upload size={12} /> Upload PDF Paper</>
                                    )}
                                  </label>
                                </div>
                              )}
                              {round.advanced_mode && round.question_paper_url && (
                                <a
                                  href={round.question_paper_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="px-2 py-1 text-xs bg-emerald-100 text-emerald-700 rounded flex items-center gap-1 hover:bg-emerald-200"
                                >
                                  <Eye size={12} /> View PDF Paper
                                </a>
                              )}
                              
                              {/* Edit/Delete */}
                              <button
                                data-testid={`edit-round-${round.id}`}
                                onClick={() => {
                                  setEditingRound(round);
                                  setRoundForm({ 
                                    name: round.name, 
                                    duration_minutes: round.duration_minutes, 
                                    status: round.status,
                                    cutoff_marks: round.cutoff_marks || null,
                                    total_marks: round.total_marks || null,
                                    access_type: round.access_type || 'public',
                                    // Navigation settings
                                    allow_navigation: round.allow_navigation !== false,  // Default to true
                                    lock_answers_on_proceed: round.lock_answers_on_proceed || false,  // Lock answers when proceeding
                                    // Advanced Assessment Mode fields
                                    advanced_mode: round.advanced_mode || false,
                                    question_format: round.question_format || 'standard',
                                    submission_type: round.submission_type || 'standard',
                                    security_2fa: round.security_2fa || 'none',
                                    allow_pdf_submission: round.allow_pdf_submission || false,
                                    allow_image_submission: round.allow_image_submission || false,
                                    max_file_size_mb: round.max_file_size_mb || 10
                                  });
                                  setShowRoundModal(true);
                                }}
                                className="p-1.5 text-gray-500 hover:bg-gray-200 rounded"
                                title="Edit round"
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                onClick={() => deleteRound(round.id)}
                                className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                          
                          {/* Round Analytics - Quick Stats */}
                          {(round.status === 'live' || round.status === 'completed') && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              {!analytics ? (
                                <button
                                  onClick={() => fetchRoundAnalytics(round.id)}
                                  disabled={loadingAnalytics === round.id}
                                  className="text-xs text-purple-600 hover:underline flex items-center gap-1"
                                >
                                  {loadingAnalytics === round.id ? (
                                    <><Loader2 size={12} className="animate-spin" /> Loading...</>
                                  ) : (
                                    <><BarChart3 size={12} /> View Analytics</>
                                  )}
                                </button>
                              ) : (
                                <div className="grid grid-cols-6 gap-2 text-center">
                                  <div className="bg-white rounded p-2 border">
                                    <p className="text-lg font-bold text-gray-900">{analytics.stats?.total_eligible || 0}</p>
                                    <p className="text-xs text-gray-500">Total</p>
                                  </div>
                                  <div className="bg-white rounded p-2 border">
                                    <p className="text-lg font-bold text-blue-600">{analytics.stats?.attempted || 0}</p>
                                    <p className="text-xs text-gray-500">Attempted</p>
                                  </div>
                                  <div className="bg-white rounded p-2 border">
                                    <p className="text-lg font-bold text-green-600">{analytics.stats?.qualified || 0}</p>
                                    <p className="text-xs text-gray-500">Qualified</p>
                                  </div>
                                  <div className="bg-white rounded p-2 border">
                                    <p className="text-lg font-bold text-red-600">{analytics.stats?.rejected || 0}</p>
                                    <p className="text-xs text-gray-500">Rejected</p>
                                  </div>
                                  <div className="bg-white rounded p-2 border">
                                    <p className="text-lg font-bold text-purple-600">{analytics.scores?.average || 0}</p>
                                    <p className="text-xs text-gray-500">Avg Score</p>
                                  </div>
                                  <div className="bg-white rounded p-2 border">
                                    <p className="text-lg font-bold text-amber-600">{analytics.scores?.highest || 0}</p>
                                    <p className="text-xs text-gray-500">Top Score</p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          {/* Expand/Collapse for Round Details */}
                          <button
                            onClick={() => {
                              if (!isExpanded) {
                                // When expanding, fetch access data if needed
                                if (round.access_type === 'pre_approved' && !roundApprovedEmails[round.id]) {
                                  fetchRoundApprovedList(round.id);
                                }
                                if ((round.access_type === 'invite' || round.access_type === 'invite_only') && !roundInvites[round.id]) {
                                  fetchRoundInvites(round.id);
                                }
                              }
                              setExpandedRound(isExpanded ? null : round.id);
                            }}
                            className="mt-3 w-full text-xs text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1 pt-2 border-t border-gray-200"
                          >
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            {isExpanded ? 'Collapse' : `Expand (${roundQuestions.length} questions)`}
                          </button>
                        </div>
                        
                        {/* Round Expanded Content with Tabs */}
                        {isExpanded && (
                          <div className="border-t bg-white">
                            {/* Tab Navigation */}
                            <div className="flex border-b bg-gray-50">
                              <button
                                onClick={() => setRoundActiveTab(prev => ({ ...prev, [round.id]: 'questions' }))}
                                className={`px-4 py-2 text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors ${
                                  (roundActiveTab[round.id] || 'questions') === 'questions'
                                    ? 'border-purple-600 text-purple-600 bg-white'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                              >
                                <FileText size={12} /> Questions ({roundQuestions.length})
                              </button>
                              <button
                                onClick={() => {
                                  setRoundActiveTab(prev => ({ ...prev, [round.id]: 'access' }));
                                  // Fetch access data if not loaded
                                  if (round.access_type === 'pre_approved' && !roundApprovedEmails[round.id]) {
                                    fetchRoundApprovedList(round.id);
                                  }
                                  if ((round.access_type === 'invite' || round.access_type === 'invite_only') && !roundInvites[round.id]) {
                                    fetchRoundInvites(round.id);
                                  }
                                }}
                                className={`px-4 py-2 text-xs font-medium flex items-center gap-1.5 border-b-2 transition-colors ${
                                  roundActiveTab[round.id] === 'access'
                                    ? 'border-purple-600 text-purple-600 bg-white'
                                    : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                              >
                                <Key size={12} /> Access Control
                              </button>
                            </div>

                            {/* Questions Tab Content */}
                            {(roundActiveTab[round.id] || 'questions') === 'questions' && (
                              <>
                                <div className="flex items-center justify-between px-4 py-2 bg-gray-100 border-b">
                                  <span className="text-xs font-medium text-gray-600">Questions in this round</span>
                                  <button
                                    onClick={() => {
                                      setSelectedRound(round);
                                      setEditingQuestion(null);
                                      resetQuestionForm();
                                      setShowQuestionModal(true);
                                    }}
                                    className="text-xs px-2 py-1 bg-purple-100 text-purple-600 rounded hover:bg-purple-200 flex items-center gap-1"
                                  >
                                    <Plus size={12} /> Add
                                  </button>
                                </div>
                                {roundQuestions.length === 0 ? (
                                  <div className="p-4 text-center text-gray-400 text-sm">
                                    No questions in this round yet
                                  </div>
                                ) : (
                                  <DndContext
                                    sensors={sensors}
                                    collisionDetection={closestCenter}
                                    onDragEnd={(event) => handleDragEnd(event, round.id)}
                                  >
                                    <SortableContext
                                      items={roundQuestions.map(q => q.id)}
                                      strategy={verticalListSortingStrategy}
                                    >
                                      <div className="p-2 space-y-2 max-h-96 overflow-y-auto">
                                        {roundQuestions.map((q) => (
                                          <SortableQuestion
                                            key={q.id}
                                            question={q}
                                            round={round}
                                            onUnassign={unassignQuestionFromRound}
                                            onEdit={(question) => {
                                              setEditingQuestion(question);
                                              setSelectedRound(round);
                                              setQuestionForm({
                                                type: question.type || 'mcq',
                                                title: question.title || question.problem_statement || '',
                                                options: question.options || ['', '', '', ''],
                                                correct_answer: question.correct_answer || '',
                                                points: question.points || 10,
                                                image_url: question.image_url || null,
                                                option_images: question.option_images || {},
                                                word_limit: question.word_limit || 500,
                                                expected_answer_hints: question.expected_answer_hints || '',
                                                expected_answer: question.expected_answer || '',
                                                problem_statement: question.problem_statement || question.title || '',
                                                input_format: question.input_format || '',
                                                output_format: question.output_format || '',
                                                constraints: question.constraints || '',
                                                sample_input: question.sample_input || '',
                                                sample_output: question.sample_output || '',
                                                difficulty: question.difficulty || 'medium',
                                                test_cases: (question.test_cases && question.test_cases.length > 0) 
                                                  ? question.test_cases 
                                                  : [{ input: '', expected_output: '', is_hidden: false }]
                                              });
                                              setShowQuestionModal(true);
                                            }}
                                            onDelete={deleteQuestion}
                                          />
                                        ))}
                                      </div>
                                    </SortableContext>
                                  </DndContext>
                                )}
                              </>
                            )}

                            {/* Access Control Tab Content */}
                            {roundActiveTab[round.id] === 'access' && (
                              <div className="p-4">
                                {/* Access Type Selector */}
                                <div className="mb-4">
                                  <label className="block text-xs font-medium text-gray-700 mb-2">Access Type for this Round</label>
                                  <div className="grid grid-cols-3 gap-2">
                                    <button
                                      onClick={() => changeRoundAccessType(round.id, 'public')}
                                      className={`p-2 border rounded-lg text-center text-xs transition-colors ${
                                        round.access_type === 'public' 
                                          ? 'border-green-500 bg-green-50 text-green-700' 
                                          : 'border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      <Globe size={16} className="mx-auto mb-1" />
                                      Public
                                    </button>
                                    <button
                                      onClick={() => {
                                        changeRoundAccessType(round.id, 'pre_approved');
                                        if (!roundApprovedEmails[round.id]) {
                                          fetchRoundApprovedList(round.id);
                                        }
                                      }}
                                      className={`p-2 border rounded-lg text-center text-xs transition-colors ${
                                        round.access_type === 'pre_approved' 
                                          ? 'border-blue-500 bg-blue-50 text-blue-700' 
                                          : 'border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      <Users size={16} className="mx-auto mb-1" />
                                      Pre-Approved
                                    </button>
                                    <button
                                      onClick={() => {
                                        changeRoundAccessType(round.id, 'invite');
                                        if (!roundInvites[round.id]) {
                                          fetchRoundInvites(round.id);
                                        }
                                      }}
                                      className={`p-2 border rounded-lg text-center text-xs transition-colors ${
                                        (round.access_type === 'invite' || round.access_type === 'invite_only')
                                          ? 'border-amber-500 bg-amber-50 text-amber-700' 
                                          : 'border-gray-200 hover:border-gray-300'
                                      }`}
                                    >
                                      <UserCheck size={16} className="mx-auto mb-1" />
                                      Invite Only
                                    </button>
                                  </div>
                                </div>

                                {/* Public Mode Info */}
                                {round.access_type === 'public' && (
                                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-700">
                                    <Globe size={14} className="inline mr-2" />
                                    Anyone who has registered for the challenge can access this round.
                                  </div>
                                )}

                                {/* Pre-Approved Mode */}
                                {round.access_type === 'pre_approved' && (
                                  <div className="space-y-3">
                                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700 mb-3">
                                      <Users size={14} className="inline mr-2" />
                                      Only candidates in the approved list below can access this round.
                                    </div>
                                    
                                    {/* Add Email Section */}
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-xs font-medium text-gray-700">Approved List ({(roundApprovedEmails[round.id] || []).length})</span>
                                      <button
                                        onClick={() => setShowRoundBulkInput(!showRoundBulkInput)}
                                        className="text-xs text-purple-600 hover:text-purple-700"
                                      >
                                        {showRoundBulkInput ? 'Single Add' : 'Bulk Add'}
                                      </button>
                                    </div>
                                    
                                    {!showRoundBulkInput ? (
                                      <div className="flex gap-2">
                                        <input
                                          type="text"
                                          value={roundNewName}
                                          onChange={(e) => setRoundNewName(e.target.value)}
                                          placeholder="Name"
                                          className="w-1/3 px-2 py-1.5 border rounded text-sm"
                                        />
                                        <input
                                          type="email"
                                          value={roundNewEmail}
                                          onChange={(e) => setRoundNewEmail(e.target.value)}
                                          placeholder="email@example.com"
                                          className="flex-1 px-2 py-1.5 border rounded text-sm"
                                        />
                                        <button
                                          onClick={() => addRoundApprovedEmail(round.id)}
                                          disabled={!roundNewEmail.trim()}
                                          className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                                        >
                                          Add
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        <textarea
                                          value={roundBulkEmails}
                                          onChange={(e) => setRoundBulkEmails(e.target.value)}
                                          placeholder="Paste comma-separated emails..."
                                          className="w-full px-2 py-1.5 border rounded text-sm h-20 font-mono resize-none"
                                        />
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs text-gray-500">
                                            {roundBulkEmails.trim() 
                                              ? `${roundBulkEmails.split(/[,\n;]+/).filter(e => e.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())).length} valid emails`
                                              : 'Separate with commas, newlines, or semicolons'}
                                          </span>
                                          <button
                                            onClick={() => addRoundBulkEmails(round.id)}
                                            disabled={!roundBulkEmails.trim()}
                                            className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50"
                                          >
                                            Add All
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* Approved List */}
                                    {loadingRoundAccess === round.id ? (
                                      <div className="py-4 text-center text-gray-400 text-sm">
                                        <Loader2 size={16} className="animate-spin inline mr-2" /> Loading...
                                      </div>
                                    ) : (roundApprovedEmails[round.id] || []).length > 0 ? (
                                      <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                                        {(roundApprovedEmails[round.id] || []).map((entry) => (
                                          <div key={entry.id || entry.email} className="flex items-center justify-between px-3 py-2 text-sm">
                                            <div>
                                              <span className="text-gray-900">{entry.email}</span>
                                              {entry.name && <span className="text-gray-500 ml-2">({entry.name})</span>}
                                            </div>
                                            <button
                                              onClick={() => deleteRoundApprovedEntry(round.id, entry.id)}
                                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                                            >
                                              <X size={14} />
                                            </button>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="py-4 text-center text-gray-400 text-sm border rounded-lg">
                                        No approved candidates yet
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Invite Only Mode */}
                                {(round.access_type === 'invite' || round.access_type === 'invite_only') && (
                                  <div className="space-y-3">
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 mb-3">
                                      <UserCheck size={14} className="inline mr-2" />
                                      Candidates need a unique invite link to access this round.
                                    </div>
                                    
                                    {/* Create Invite */}
                                    <div className="flex gap-2">
                                      <input
                                        type="email"
                                        value={roundInviteEmail}
                                        onChange={(e) => setRoundInviteEmail(e.target.value)}
                                        placeholder="Invite email@example.com"
                                        className="flex-1 px-2 py-1.5 border rounded text-sm"
                                      />
                                      <button
                                        onClick={() => createRoundInvite(round.id)}
                                        disabled={!roundInviteEmail.trim()}
                                        className="px-3 py-1.5 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
                                      >
                                        <Plus size={12} /> Invite
                                      </button>
                                    </div>

                                    {/* Invites List */}
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-xs font-medium text-gray-700">Invites ({(roundInvites[round.id] || []).length})</span>
                                    </div>
                                    
                                    {loadingRoundAccess === round.id ? (
                                      <div className="py-4 text-center text-gray-400 text-sm">
                                        <Loader2 size={16} className="animate-spin inline mr-2" /> Loading...
                                      </div>
                                    ) : (roundInvites[round.id] || []).length > 0 ? (
                                      <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                                        {(roundInvites[round.id] || []).map((invite) => (
                                          <div key={invite.id || invite.email} className="flex items-center justify-between px-3 py-2 text-sm">
                                            <div className="flex-1 min-w-0">
                                              <span className="text-gray-900 block truncate">{invite.email}</span>
                                              <span className={`text-xs ${invite.status === 'used' ? 'text-green-600' : 'text-amber-600'}`}>
                                                {invite.status === 'used' ? 'Used' : 'Pending'}
                                              </span>
                                            </div>
                                            <div className="flex items-center gap-1 ml-2">
                                              <button
                                                onClick={() => copyRoundInviteLink(round, invite)}
                                                className="p-1 text-blue-500 hover:bg-blue-50 rounded"
                                                title="Copy invite link"
                                              >
                                                <Copy size={14} />
                                              </button>
                                              <button
                                                onClick={() => deleteRoundInvite(round.id, invite.id)}
                                                className="p-1 text-red-500 hover:bg-red-50 rounded"
                                                title="Revoke invite"
                                              >
                                                <X size={14} />
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="py-4 text-center text-gray-400 text-sm border rounded-lg">
                                        No invites sent yet
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Next Round Hint */}
                        {round.status === 'completed' && idx < rounds.length - 1 && rounds[idx + 1].status === 'draft' && (
                          <div className="px-4 py-2 bg-purple-50 border-t text-xs text-purple-700 flex items-center gap-2">
                            <ArrowLeft size={12} className="rotate-180" />
                            Round {idx + 1} completed. Ready to start Round {idx + 2}!
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Unassigned Questions */}
              {getUnassignedQuestions().length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-gray-700 flex items-center gap-2">
                      <AlertCircle size={14} className="text-amber-500" />
                      Unassigned Questions ({getUnassignedQuestions().length})
                    </p>
                    <div className="flex items-center gap-2">
                      {selectedUnassigned.size > 0 && (
                        <>
                          <span className="text-xs text-purple-600 font-medium">
                            {selectedUnassigned.size} selected
                          </span>
                          <button
                            onClick={() => setShowBulkAssignModal(true)}
                            className="px-3 py-1.5 bg-purple-600 text-white rounded text-xs font-medium hover:bg-purple-700 flex items-center gap-1"
                          >
                            <CheckSquare size={12} />
                            Bulk Assign
                          </button>
                        </>
                      )}
                      <button
                        onClick={selectedUnassigned.size === getUnassignedQuestions().length ? deselectAllUnassigned : selectAllUnassigned}
                        className="text-xs text-purple-600 hover:underline"
                      >
                        {selectedUnassigned.size === getUnassignedQuestions().length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {getUnassignedQuestions().map((q) => (
                      <div key={q.id} className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                        selectedUnassigned.has(q.id) 
                          ? 'bg-purple-50 border-purple-300' 
                          : 'bg-amber-50 border-amber-200 hover:bg-amber-100'
                      }`}>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {/* Checkbox */}
                          <button
                            onClick={() => toggleUnassignedSelection(q.id)}
                            className={`p-0.5 rounded transition-colors ${
                              selectedUnassigned.has(q.id) 
                                ? 'text-purple-600' 
                                : 'text-gray-400 hover:text-gray-600'
                            }`}
                          >
                            {selectedUnassigned.has(q.id) ? (
                              <CheckSquare size={18} />
                            ) : (
                              <Square size={18} />
                            )}
                          </button>
                          <span className={`px-1.5 py-0.5 rounded text-xs flex-shrink-0 ${
                            q.type === 'mcq' ? 'bg-cyan-100 text-cyan-700' : 
                            q.type === 'subjective' ? 'bg-amber-100 text-amber-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {q.type?.toUpperCase() || 'MCQ'}
                          </span>
                          <span className="text-sm text-gray-700 truncate">
                            {q.title || q.problem_statement || 'Untitled'}
                          </span>
                          <span className="text-xs text-purple-600 flex-shrink-0">{q.points} pts</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                          {/* Assign to Round Dropdown */}
                          {rounds.length > 0 && (
                            <select
                              onChange={(e) => {
                                if (e.target.value) {
                                  assignQuestionToRound(q.id, e.target.value);
                                  e.target.value = ''; // Reset select
                                }
                              }}
                              className="text-xs px-2 py-1 border border-purple-300 rounded bg-white text-purple-700 cursor-pointer hover:bg-purple-50 focus:outline-none focus:ring-1 focus:ring-purple-500"
                              defaultValue=""
                            >
                              <option value="" disabled>Assign</option>
                              {rounds.map((r, idx) => (
                                <option key={r.id} value={r.id}>
                                  R{idx + 1}: {r.name?.substring(0, 15) || `Round ${idx + 1}`}
                                </option>
                              ))}
                            </select>
                          )}
                          <button
                            onClick={() => {
                              setEditingQuestion(q);
                              setQuestionForm({
                                type: q.type || 'mcq',
                                title: q.title || q.problem_statement || '',
                                options: q.options || ['', '', '', ''],
                                correct_answer: q.correct_answer || '',
                                points: q.points || 10,
                                image_url: q.image_url || null,
                                option_images: q.option_images || {},
                                word_limit: q.word_limit || 500,
                                expected_answer_hints: q.expected_answer_hints || '',
                                expected_answer: q.expected_answer || '',
                                problem_statement: q.problem_statement || q.title || '',
                                input_format: q.input_format || '',
                                output_format: q.output_format || '',
                                constraints: q.constraints || '',
                                sample_input: q.sample_input || '',
                                sample_output: q.sample_output || '',
                                difficulty: q.difficulty || 'medium',
                                test_cases: (q.test_cases && q.test_cases.length > 0) 
                                  ? q.test_cases 
                                  : [{ input: '', expected_output: '', is_hidden: false }]
                              });
                              setShowQuestionModal(true);
                            }}
                            className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                            title="Edit question"
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={() => deleteQuestion(q.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                            title="Delete question"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Round Modal */}
      {showRoundModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Play size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {editingRound ? 'Edit Round' : 'Create New Round'}
                    </h3>
                    <p className="text-purple-200 text-xs">Configure assessment round settings</p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowRoundModal(false); setEditingRound(null); setRoundForm({ name: '', duration_minutes: 30, status: 'live', cutoff_marks: null, total_marks: null, access_type: 'public', allow_navigation: true, advanced_mode: false, question_format: 'standard', submission_type: 'standard', security_2fa: 'none', allow_pdf_submission: false, allow_image_submission: false, max_file_size_mb: 10 }); }}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
              {/* Round Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">Round Name</label>
                <input
                  type="text"
                  value={roundForm.name}
                  onChange={(e) => setRoundForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all outline-none"
                  placeholder="e.g. Round 1 - Aptitude Test"
                />
              </div>
              
              {/* Duration & Status Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    <Clock size={14} className="inline mr-1.5 text-purple-600" />
                    Duration
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      value={roundForm.duration_minutes}
                      onChange={(e) => setRoundForm(prev => ({ ...prev, duration_minutes: parseInt(e.target.value) || 30 }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all outline-none pr-16"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">mins</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">Status</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setRoundForm(prev => ({ ...prev, status: 'live' }))}
                      className={`py-3 px-3 rounded-xl text-sm font-medium transition-all ${
                        roundForm.status === 'live'
                          ? 'bg-green-100 text-green-700 border-2 border-green-400'
                          : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:border-gray-200'
                      }`}
                    >
                      <Play size={14} className="inline mr-1" /> Live
                    </button>
                    <button
                      type="button"
                      onClick={() => setRoundForm(prev => ({ ...prev, status: 'draft' }))}
                      className={`py-3 px-3 rounded-xl text-sm font-medium transition-all ${
                        roundForm.status === 'draft'
                          ? 'bg-gray-200 text-gray-700 border-2 border-gray-400'
                          : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:border-gray-200'
                      }`}
                    >
                      <Pause size={14} className="inline mr-1" /> Draft
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Question Navigation Control */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <ChevronLeft size={16} className="text-blue-600" />
                      <ChevronRight size={16} className="text-blue-600 -ml-1" />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-800">Allow Question Navigation</label>
                      <p className="text-xs text-gray-500">Candidates can move back and forth between questions</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRoundForm(prev => ({ ...prev, allow_navigation: !prev.allow_navigation }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      roundForm.allow_navigation ? 'bg-blue-500' : 'bg-gray-300'
                    }`}
                    data-testid="allow-navigation-toggle"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                      roundForm.allow_navigation ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                {!roundForm.allow_navigation && (
                  <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-xs text-amber-700 flex items-center gap-1.5">
                      <AlertTriangle size={12} />
                      Sequential mode: Candidates can only proceed forward, no going back to previous questions
                    </p>
                  </div>
                )}
              </div>
              
              {/* Lock Answers on Proceed - Only shown when navigation is disabled */}
              {!roundForm.allow_navigation && (
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-4 border border-orange-100">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Lock size={18} className="text-orange-600" />
                      </div>
                      <div>
                        <label className="text-sm font-semibold text-gray-800">Lock Answers on Proceed</label>
                        <p className="text-xs text-gray-500">Answers become final when moving to next question</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setRoundForm(prev => ({ ...prev, lock_answers_on_proceed: !prev.lock_answers_on_proceed }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        roundForm.lock_answers_on_proceed ? 'bg-orange-500' : 'bg-gray-300'
                      }`}
                      data-testid="lock-answers-toggle"
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                        roundForm.lock_answers_on_proceed ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                  {roundForm.lock_answers_on_proceed && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-lg">
                      <p className="text-xs text-red-700 flex items-center gap-1.5">
                        <AlertTriangle size={12} />
                        Strict mode: Once candidates proceed, they cannot change their previous answers
                      </p>
                    </div>
                  )}
                </div>
              )}
              
              {/* Access Type */}
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  <Key size={14} className="inline mr-1.5 text-purple-600" />
                  Access Type
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setRoundForm(prev => ({ ...prev, access_type: 'public' }))}
                    className={`p-4 border-2 rounded-xl text-center transition-all ${
                      roundForm.access_type === 'public' 
                        ? 'border-green-500 bg-green-50 shadow-md shadow-green-100' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                      roundForm.access_type === 'public' ? 'bg-green-100' : 'bg-gray-100'
                    }`}>
                      <Globe size={20} className={roundForm.access_type === 'public' ? 'text-green-600' : 'text-gray-500'} />
                    </div>
                    <span className={`text-sm font-medium ${roundForm.access_type === 'public' ? 'text-green-700' : 'text-gray-700'}`}>Public</span>
                    <p className="text-xs text-gray-400 mt-1">Anyone can join</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoundForm(prev => ({ ...prev, access_type: 'pre_approved' }))}
                    className={`p-4 border-2 rounded-xl text-center transition-all ${
                      roundForm.access_type === 'pre_approved' 
                        ? 'border-blue-500 bg-blue-50 shadow-md shadow-blue-100' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                      roundForm.access_type === 'pre_approved' ? 'bg-blue-100' : 'bg-gray-100'
                    }`}>
                      <Users size={20} className={roundForm.access_type === 'pre_approved' ? 'text-blue-600' : 'text-gray-500'} />
                    </div>
                    <span className={`text-sm font-medium ${roundForm.access_type === 'pre_approved' ? 'text-blue-700' : 'text-gray-700'}`}>Pre-Approved</span>
                    <p className="text-xs text-gray-400 mt-1">Whitelist only</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setRoundForm(prev => ({ ...prev, access_type: 'invite' }))}
                    className={`p-4 border-2 rounded-xl text-center transition-all ${
                      roundForm.access_type === 'invite' 
                        ? 'border-amber-500 bg-amber-50 shadow-md shadow-amber-100' 
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-10 h-10 mx-auto mb-2 rounded-full flex items-center justify-center ${
                      roundForm.access_type === 'invite' ? 'bg-amber-100' : 'bg-gray-100'
                    }`}>
                      <UserCheck size={20} className={roundForm.access_type === 'invite' ? 'text-amber-600' : 'text-gray-500'} />
                    </div>
                    <span className={`text-sm font-medium ${roundForm.access_type === 'invite' ? 'text-amber-700' : 'text-gray-700'}`}>Invite Only</span>
                    <p className="text-xs text-gray-400 mt-1">Unique links</p>
                  </button>
                </div>
              </div>
              
              {/* Scoring Section */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-4 border border-purple-100">
                <label className="block text-sm font-semibold text-gray-800 mb-3">
                  <Target size={14} className="inline mr-1.5 text-purple-600" />
                  Scoring Configuration
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Total Marks</label>
                    <input
                      type="number"
                      value={roundForm.total_marks || ''}
                      onChange={(e) => setRoundForm(prev => ({ ...prev, total_marks: e.target.value ? parseInt(e.target.value) : null }))}
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-1 focus:ring-purple-100 transition-all outline-none text-sm"
                      placeholder="Auto from questions"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Cutoff (Pass Mark)</label>
                    <input
                      type="number"
                      value={roundForm.cutoff_marks || ''}
                      onChange={(e) => setRoundForm(prev => ({ ...prev, cutoff_marks: e.target.value ? parseInt(e.target.value) : null }))}
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:border-purple-400 focus:ring-1 focus:ring-purple-100 transition-all outline-none text-sm"
                      placeholder="Minimum to pass"
                    />
                  </div>
                </div>
                {roundForm.cutoff_marks && roundForm.total_marks && (
                  <div className="mt-3 flex items-center gap-2 text-sm">
                    <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-indigo-500 h-full rounded-full transition-all"
                        style={{ width: `${Math.min(100, Math.round(roundForm.cutoff_marks / roundForm.total_marks * 100))}%` }}
                      />
                    </div>
                    <span className="text-purple-700 font-semibold whitespace-nowrap">
                      {Math.round(roundForm.cutoff_marks / roundForm.total_marks * 100)}% to pass
                    </span>
                  </div>
                )}
              </div>
              
              {/* Advanced Assessment Mode Toggle */}
              <div className="border-2 border-dashed border-amber-200 rounded-xl p-4 bg-amber-50/50">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                      <Settings size={16} className="text-amber-600" />
                    </div>
                    <div>
                      <label className="text-sm font-semibold text-gray-800">Advanced Assessment Mode</label>
                      <p className="text-xs text-gray-500">Enable PDF uploads, image submissions & 2FA</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setRoundForm(prev => ({ ...prev, advanced_mode: !prev.advanced_mode }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      roundForm.advanced_mode ? 'bg-amber-500' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                      roundForm.advanced_mode ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {roundForm.advanced_mode && (
                  <div className="space-y-4 pt-3 border-t border-amber-200">
                    {/* Question Format */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2">Question Format</label>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => setRoundForm(prev => ({ ...prev, question_format: 'standard' }))}
                          className={`p-2 rounded-lg text-xs font-medium transition-all ${
                            roundForm.question_format === 'standard' 
                              ? 'bg-amber-500 text-white' 
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-amber-300'
                          }`}
                        >
                          Standard
                        </button>
                        <button
                          type="button"
                          onClick={() => setRoundForm(prev => ({ ...prev, question_format: 'latex' }))}
                          className={`p-2 rounded-lg text-xs font-medium transition-all ${
                            roundForm.question_format === 'latex' 
                              ? 'bg-amber-500 text-white' 
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-amber-300'
                          }`}
                        >
                          LaTeX
                        </button>
                        <button
                          type="button"
                          onClick={() => setRoundForm(prev => ({ ...prev, question_format: 'pdf' }))}
                          className={`p-2 rounded-lg text-xs font-medium transition-all ${
                            roundForm.question_format === 'pdf' 
                              ? 'bg-amber-500 text-white' 
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-amber-300'
                          }`}
                        >
                          PDF Paper
                        </button>
                      </div>
                    </div>
                    
                    {/* Submission Type */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2">Submission Type</label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-amber-300">
                          <input
                            type="checkbox"
                            checked={roundForm.allow_pdf_submission}
                            onChange={(e) => setRoundForm(prev => ({ ...prev, allow_pdf_submission: e.target.checked }))}
                            className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                          />
                          <span className="text-xs text-gray-700">PDF Upload</span>
                        </label>
                        <label className="flex items-center gap-2 p-2 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-amber-300">
                          <input
                            type="checkbox"
                            checked={roundForm.allow_image_submission}
                            onChange={(e) => setRoundForm(prev => ({ ...prev, allow_image_submission: e.target.checked }))}
                            className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                          />
                          <span className="text-xs text-gray-700">Image Upload</span>
                        </label>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Allow candidates to upload handwritten/scanned answers</p>
                    </div>
                    
                    {/* Security - 2FA */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 mb-2">Security (2FA)</label>
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => setRoundForm(prev => ({ ...prev, security_2fa: 'none' }))}
                          className={`p-2 rounded-lg text-xs font-medium transition-all ${
                            roundForm.security_2fa === 'none' 
                              ? 'bg-gray-600 text-white' 
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          No 2FA
                        </button>
                        <button
                          type="button"
                          onClick={() => setRoundForm(prev => ({ ...prev, security_2fa: 'email_otp' }))}
                          className={`p-2 rounded-lg text-xs font-medium transition-all ${
                            roundForm.security_2fa === 'email_otp' 
                              ? 'bg-green-500 text-white' 
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-green-300'
                          }`}
                        >
                          Email OTP
                        </button>
                      </div>
                      {roundForm.security_2fa === 'email_otp' && (
                        <p className="text-xs text-green-600 mt-1">✓ Candidates will verify via email OTP before starting</p>
                      )}
                    </div>
                    
                    {/* Max File Size */}
                    {(roundForm.allow_pdf_submission || roundForm.allow_image_submission) && (
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 mb-1">Max File Size</label>
                        <select
                          value={roundForm.max_file_size_mb}
                          onChange={(e) => setRoundForm(prev => ({ ...prev, max_file_size_mb: parseInt(e.target.value) }))}
                          className="w-full px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:border-amber-400 outline-none"
                        >
                          <option value={5}>5 MB</option>
                          <option value={10}>10 MB</option>
                          <option value={15}>15 MB</option>
                          <option value={20}>20 MB</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button
                type="button"
                onClick={() => { setShowRoundModal(false); setEditingRound(null); setRoundForm({ name: '', duration_minutes: 30, status: 'live', cutoff_marks: null, total_marks: null, access_type: 'public', allow_navigation: true, advanced_mode: false, question_format: 'standard', submission_type: 'standard', security_2fa: 'none', allow_pdf_submission: false, allow_image_submission: false, max_file_size_mb: 10 }); }}
                className="px-5 py-2.5 text-gray-600 hover:bg-gray-200 rounded-xl font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveRound}
                disabled={!roundForm.name.trim()}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-purple-200 disabled:shadow-none flex items-center gap-2"
              >
                {editingRound ? (
                  <><CheckCircle size={18} /> Update Round</>
                ) : (
                  <><Plus size={18} /> Create Round</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Question Modal - Enhanced UI */}
      {showQuestionModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-600 to-blue-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    {questionForm.type === 'mcq' ? <List size={20} className="text-white" /> :
                     questionForm.type === 'subjective' ? <FileEdit size={20} className="text-white" /> :
                     questionForm.type === 'pdf' ? <FileText size={20} className="text-white" /> :
                     <Code size={20} className="text-white" />}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">
                      {editingQuestion ? 'Edit Question' : 'Add New Question'}
                    </h3>
                    <p className="text-cyan-200 text-xs">
                      {questionForm.type === 'mcq' ? 'Multiple Choice Question' :
                       questionForm.type === 'subjective' ? 'Subjective Answer' :
                       questionForm.type === 'pdf' ? 'PDF Question Paper' : 'Coding Problem'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowQuestionModal(false); setEditingQuestion(null); resetQuestionForm(); }}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
                  disabled={savingQuestion}
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6 max-h-[65vh] overflow-y-auto space-y-5">
              {/* Round & Type Selection Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">Add to Round</label>
                  <select
                    value={selectedRound?.id || ''}
                    onChange={(e) => setSelectedRound(rounds.find(r => r.id === e.target.value) || null)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 transition-all outline-none bg-white"
                  >
                    <option value="">Select round...</option>
                    {rounds.map(r => (
                      <option key={r.id} value={r.id}>{r.name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-800 mb-2">Question Type</label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { type: 'mcq', icon: List, label: 'MCQ', color: 'cyan' },
                      { type: 'subjective', icon: FileEdit, label: 'Text', color: 'amber' },
                      { type: 'coding', icon: Code, label: 'Code', color: 'green' },
                      { type: 'pdf', icon: FileText, label: 'PDF', color: 'red' }
                    ].map(({ type, icon: Icon, label, color }) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setQuestionForm(prev => ({ ...prev, type }))}
                        className={`p-2.5 border-2 rounded-xl text-center transition-all ${
                          questionForm.type === type
                            ? `border-${color}-500 bg-${color}-50 text-${color}-700`
                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                        }`}
                        style={questionForm.type === type ? {
                          borderColor: color === 'cyan' ? '#06b6d4' : color === 'amber' ? '#f59e0b' : color === 'green' ? '#22c55e' : '#ef4444',
                          backgroundColor: color === 'cyan' ? '#ecfeff' : color === 'amber' ? '#fffbeb' : color === 'green' ? '#f0fdf4' : '#fef2f2'
                        } : {}}
                      >
                        <Icon size={18} className="mx-auto mb-0.5" />
                        <span className="text-xs font-medium">{label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Question Title/Statement - Not shown for PDF type which has its own section */}
              {questionForm.type !== 'pdf' && (
              <div>
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  {questionForm.type === 'coding' ? 'Problem Statement' : 'Question'}
                </label>
                <div className="relative">
                  <textarea
                    value={questionForm.title}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 transition-all outline-none h-28 font-mono text-sm resize-none"
                    placeholder={questionForm.type === 'coding' 
                      ? "Describe the problem clearly..."
                      : "Enter question text..."}
                  />
                  <div className="absolute bottom-3 right-3 flex items-center gap-2">
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded">LaTeX: $x^2$</span>
                  </div>
                </div>
                {/* LaTeX Preview */}
                {questionForm.title && questionForm.title.includes('$') && (
                  <div className="mt-2 p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border">
                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                      <Eye size={12} /> Preview
                    </p>
                    <div className="text-sm text-gray-800">
                      <LaTeXRenderer content={questionForm.title} />
                    </div>
                  </div>
                )}
              </div>
              )}
              
              {/* Image Upload - Not shown for PDF type */}
              {questionForm.type !== 'pdf' && (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                <label className="block text-sm font-semibold text-gray-800 mb-2">
                  <ImageIcon size={14} className="inline mr-1.5 text-cyan-600" />
                  Question Image
                  <span className="text-xs font-normal text-gray-400 ml-1">(optional)</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                />
                {!questionForm.image_url ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="w-full py-6 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-cyan-400 hover:text-cyan-600 hover:bg-cyan-50/50 transition-all flex flex-col items-center gap-2"
                  >
                    {uploadingImage ? (
                      <><Loader2 size={24} className="animate-spin" /><span className="text-sm">Uploading...</span></>
                    ) : (
                      <><Upload size={24} /><span className="text-sm">Click to upload image</span></>
                    )}
                  </button>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-white rounded-xl border">
                    <img 
                      src={`${API}/api/company/enterprise/files/${questionForm.image_url}`}
                      alt="Question" 
                      className="h-16 w-24 object-cover rounded-lg"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="flex-1">
                      <p className="text-sm text-green-600 font-medium flex items-center gap-1">
                        <CheckCircle size={14} /> Image attached
                      </p>
                    </div>
                    <button
                      onClick={() => removeImage()}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                )}
              </div>
              )}
              
              {/* MCQ Specific Fields */}
              {questionForm.type === 'mcq' && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">Answer Options</label>
                    <input
                      ref={optionFileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        if (uploadingOptionImage !== null) {
                          handleImageUpload(e.target.files[0], true, uploadingOptionImage);
                        }
                      }}
                    />
                    <div className="space-y-3">
                      {(questionForm.options || []).map((opt, idx) => (
                        <div key={idx} className={`border-2 rounded-xl p-3 transition-all ${
                          questionForm.correct_answer === opt && opt ? 'border-green-400 bg-green-50' : 'border-gray-200 bg-white'
                        }`}>
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                              questionForm.correct_answer === opt && opt 
                                ? 'bg-green-500 text-white' 
                                : 'bg-gray-100 text-gray-500'
                            }`}>
                              {String.fromCharCode(65 + idx)}
                            </div>
                            <input
                              type="text"
                              value={opt}
                              onChange={(e) => {
                                const newOpts = [...(questionForm.options || [])];
                                newOpts[idx] = e.target.value;
                                setQuestionForm(prev => ({ ...prev, options: newOpts }));
                              }}
                              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-cyan-400 focus:ring-1 focus:ring-cyan-100 outline-none"
                              placeholder={`Option ${idx + 1}`}
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setUploadingOptionImage(idx);
                                optionFileInputRef.current?.click();
                              }}
                              disabled={uploadingOptionImage === idx}
                              className={`p-2 rounded-lg border transition-all ${
                                questionForm.option_images?.[idx] 
                                  ? 'border-green-400 bg-green-50 text-green-600'
                                  : 'border-gray-200 hover:border-gray-300 text-gray-400 hover:text-gray-600'
                              }`}
                              title="Add image to option"
                            >
                              {uploadingOptionImage === idx ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : questionForm.option_images?.[idx] ? (
                                <CheckCircle size={16} />
                              ) : (
                                <ImageIcon size={16} />
                              )}
                            </button>
                            {questionForm.option_images?.[idx] && (
                              <button
                                onClick={() => removeImage(true, idx)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                              >
                                <X size={16} />
                              </button>
                            )}
                          </div>
                          {opt && opt.includes('$') && (
                            <div className="mt-2 ml-11 text-sm text-gray-600 bg-gray-50 rounded-lg px-3 py-2">
                              <LaTeXRenderer content={opt} />
                            </div>
                          )}
                          {questionForm.option_images?.[idx] && (
                            <div className="mt-2 ml-11">
                              <img 
                                src={`${API}/api/company/enterprise/files/${questionForm.option_images[idx]}`}
                                alt={`Option ${idx + 1}`}
                                className="h-16 rounded-lg border"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      <CheckCircle size={14} className="inline mr-1.5 text-green-600" />
                      Correct Answer
                    </label>
                    <select
                      value={questionForm.correct_answer}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, correct_answer: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all outline-none bg-white"
                    >
                      <option value="">Select correct answer...</option>
                      {(questionForm.options || []).filter(o => o).map((opt, idx) => (
                        <option key={idx} value={opt}>{String.fromCharCode(65 + idx)}. {opt}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              
              {/* Subjective Specific Fields */}
              {questionForm.type === 'subjective' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Word Limit</label>
                      <input
                        type="number"
                        value={questionForm.word_limit}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, word_limit: parseInt(e.target.value) || 500 }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition-all outline-none"
                        placeholder="500"
                      />
                      <p className="text-xs text-gray-500 mt-1">Maximum words allowed</p>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Key Points / Hints</label>
                      <textarea
                        value={questionForm.expected_answer_hints}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, expected_answer_hints: e.target.value }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition-all outline-none h-[76px] text-sm resize-none"
                        placeholder="Key points for evaluators..."
                      />
                    </div>
                  </div>
                  
                  {/* Expected/Model Answer Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Expected Answer / Model Answer
                      <span className="text-gray-400 font-normal ml-2">(Optional - for evaluator reference)</span>
                    </label>
                    <textarea
                      value={questionForm.expected_answer}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, expected_answer: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-100 transition-all outline-none min-h-[150px] text-sm resize-y"
                      placeholder="Enter the expected/model answer here. This will be shown to evaluators during manual scoring for reference. Supports plain text."
                    />
                    <p className="text-xs text-gray-500 mt-1">This answer will be displayed to evaluators during manual review for guidance</p>
                  </div>
                </div>
              )}
              
              {/* PDF Question Type Specific Fields */}
              {questionForm.type === 'pdf' && (
                <div className="space-y-4">
                  {/* PDF Upload Section */}
                  <div className="p-4 bg-red-50 border-2 border-dashed border-red-200 rounded-xl">
                    <input
                      type="file"
                      ref={pdfFileInputRef}
                      className="hidden"
                      accept=".pdf"
                      onChange={(e) => handlePdfQuestionUpload(e.target.files?.[0])}
                    />
                    
                    {!questionForm.pdf_url ? (
                      <div className="text-center">
                        <FileText size={40} className="mx-auto text-red-400 mb-3" />
                        <h4 className="font-semibold text-gray-800 mb-1">Upload Question Paper (PDF)</h4>
                        <p className="text-sm text-gray-500 mb-4">Upload a PDF containing the question(s) for this section</p>
                        <button
                          type="button"
                          onClick={() => pdfFileInputRef.current?.click()}
                          disabled={uploadingQuestionPdf}
                          className="px-6 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center gap-2 mx-auto"
                        >
                          {uploadingQuestionPdf ? (
                            <><Loader2 size={16} className="animate-spin" /> Uploading...</>
                          ) : (
                            <><Upload size={16} /> Select PDF</>
                          )}
                        </button>
                        <p className="text-xs text-gray-400 mt-3">Max file size: 20MB</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
                            <FileText size={24} className="text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{questionForm.pdf_file_name || 'Question Paper.pdf'}</p>
                            <p className="text-xs text-green-600 flex items-center gap-1">
                              <CheckCircle size={12} /> Uploaded successfully
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <a
                            href={questionForm.pdf_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
                            title="View PDF"
                          >
                            <Eye size={16} className="text-gray-600" />
                          </a>
                          <button
                            type="button"
                            onClick={removePdfQuestion}
                            className="p-2 bg-white border border-red-200 rounded-lg hover:bg-red-50"
                            title="Remove PDF"
                          >
                            <Trash2 size={16} className="text-red-500" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Answer Submission Options */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-3">
                      Allowed Answer Formats
                      <span className="text-gray-400 font-normal ml-2">(Students can submit answers in these formats)</span>
                    </label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={questionForm.allow_pdf_answer}
                          onChange={(e) => setQuestionForm(prev => ({ ...prev, allow_pdf_answer: e.target.checked }))}
                          className="w-4 h-4 text-red-500 rounded border-gray-300 focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700 flex items-center gap-1">
                          <FileText size={14} className="text-red-500" /> PDF Upload
                        </span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={questionForm.allow_image_answer}
                          onChange={(e) => setQuestionForm(prev => ({ ...prev, allow_image_answer: e.target.checked }))}
                          className="w-4 h-4 text-blue-500 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 flex items-center gap-1">
                          <Camera size={14} className="text-blue-500" /> Image Upload
                        </span>
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">At least one format must be selected</p>
                  </div>
                  
                  {/* Instructions for Students */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">
                      Instructions for Students
                      <span className="text-gray-400 font-normal ml-2">(Optional)</span>
                    </label>
                    <textarea
                      value={questionForm.title}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, title: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-100 transition-all outline-none h-24 text-sm resize-none"
                      placeholder="E.g., Solve all questions from the PDF. Upload your answer sheets as images or a single PDF file."
                    />
                  </div>
                </div>
              )}
              
              {/* Coding Problem Specific Fields */}
              {questionForm.type === 'coding' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Difficulty</label>
                      <div className="grid grid-cols-3 gap-2">
                        {['easy', 'medium', 'hard'].map(diff => (
                          <button
                            key={diff}
                            type="button"
                            onClick={() => setQuestionForm(prev => ({ ...prev, difficulty: diff }))}
                            className={`py-2.5 px-3 rounded-xl text-sm font-medium capitalize transition-all ${
                              questionForm.difficulty === diff
                                ? diff === 'easy' ? 'bg-green-100 text-green-700 border-2 border-green-400'
                                  : diff === 'medium' ? 'bg-amber-100 text-amber-700 border-2 border-amber-400'
                                  : 'bg-red-100 text-red-700 border-2 border-red-400'
                                : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:border-gray-200'
                            }`}
                          >
                            {diff}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Points</label>
                      <input
                        type="number"
                        value={questionForm.points}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, points: parseInt(e.target.value) || 100 }))}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-2 focus:ring-green-100 transition-all outline-none"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Input Format</label>
                      <textarea
                        value={questionForm.input_format}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, input_format: e.target.value }))}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-1 focus:ring-green-100 transition-all outline-none h-20 text-sm font-mono resize-none"
                        placeholder="Describe input format..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Output Format</label>
                      <textarea
                        value={questionForm.output_format}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, output_format: e.target.value }))}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-1 focus:ring-green-100 transition-all outline-none h-20 text-sm font-mono resize-none"
                        placeholder="Describe output format..."
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-800 mb-2">Constraints</label>
                    <textarea
                      value={questionForm.constraints}
                      onChange={(e) => setQuestionForm(prev => ({ ...prev, constraints: e.target.value }))}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-1 focus:ring-green-100 transition-all outline-none h-16 text-sm font-mono resize-none"
                      placeholder="1 ≤ N ≤ 10^5"
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Sample Input</label>
                      <textarea
                        value={questionForm.sample_input}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, sample_input: e.target.value }))}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-1 focus:ring-green-100 transition-all outline-none h-20 text-sm font-mono bg-gray-50 resize-none"
                        placeholder="5&#10;1 2 3 4 5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-800 mb-2">Sample Output</label>
                      <textarea
                        value={questionForm.sample_output}
                        onChange={(e) => setQuestionForm(prev => ({ ...prev, sample_output: e.target.value }))}
                        className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:border-green-500 focus:ring-1 focus:ring-green-100 transition-all outline-none h-20 text-sm font-mono bg-gray-50 resize-none"
                        placeholder="15"
                      />
                    </div>
                  </div>
                  
                  {/* Test Cases */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <label className="text-sm font-semibold text-gray-800">Test Cases</label>
                      <button
                        onClick={addTestCase}
                        type="button"
                        className="text-xs px-3 py-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors font-medium flex items-center gap-1"
                      >
                        <Plus size={14} /> Add Test Case
                      </button>
                    </div>
                    <div className="space-y-3 max-h-48 overflow-y-auto">
                      {(questionForm.test_cases || []).map((tc, idx) => (
                        <div key={idx} className="bg-white border border-gray-200 rounded-xl p-3">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded">#{idx + 1}</span>
                            <div className="flex items-center gap-2">
                              <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={tc?.is_hidden || false}
                                  onChange={(e) => updateTestCase(idx, 'is_hidden', e.target.checked)}
                                  className="w-3.5 h-3.5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                                />
                                <span className="text-gray-600">Hidden</span>
                              </label>
                              {(questionForm.test_cases || []).length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => removeTestCase(idx)}
                                  className="p-1 text-red-500 hover:bg-red-50 rounded"
                                >
                                  <Trash2 size={14} />
                                </button>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Input</label>
                              <textarea
                                value={tc?.input || ''}
                                onChange={(e) => updateTestCase(idx, 'input', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs font-mono h-14 resize-none focus:border-green-400 outline-none"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-gray-500 mb-1 block">Expected Output</label>
                              <textarea
                                value={tc?.expected_output || ''}
                                onChange={(e) => updateTestCase(idx, 'expected_output', e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs font-mono h-14 resize-none focus:border-green-400 outline-none"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {/* Points - for MCQ and Subjective */}
              {questionForm.type !== 'coding' && (
                <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-4 border border-cyan-100">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">
                    <Target size={14} className="inline mr-1.5 text-cyan-600" />
                    Points
                  </label>
                  <input
                    type="number"
                    value={questionForm.points}
                    onChange={(e) => setQuestionForm(prev => ({ ...prev, points: parseInt(e.target.value) || 10 }))}
                    className="w-full px-4 py-3 bg-white border border-gray-200 rounded-xl focus:border-cyan-400 focus:ring-1 focus:ring-cyan-100 transition-all outline-none"
                  />
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {selectedRound ? (
                  <span className="flex items-center gap-1.5">
                    <CheckCircle size={14} className="text-green-500" />
                    Adding to: <span className="font-medium text-gray-700">{selectedRound.name}</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-amber-600">
                    <AlertCircle size={14} />
                    Select a round first
                  </span>
                )}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowQuestionModal(false); setEditingQuestion(null); resetQuestionForm(); }}
                  className="px-5 py-2.5 text-gray-600 hover:bg-gray-200 rounded-xl font-medium transition-colors"
                  disabled={savingQuestion}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveQuestion}
                  disabled={savingQuestion || !questionForm.title.trim()}
                  className="px-6 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-xl font-medium hover:from-cyan-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-cyan-200 disabled:shadow-none flex items-center gap-2"
                >
                  {savingQuestion ? (
                    <><Loader2 size={18} className="animate-spin" /> Saving...</>
                  ) : editingQuestion ? (
                    <><CheckCircle size={18} /> Update Question</>
                  ) : (
                    <><Plus size={18} /> Add Question</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Settings Modal - Proctoring & Result Visibility */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                    <Settings size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">Challenge Settings</h3>
                    <p className="text-purple-200 text-xs">Configure proctoring and result visibility</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className="text-white/70 hover:text-white hover:bg-white/10 p-2 rounded-lg transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            
            {/* Content with Tabs */}
            <div className="max-h-[65vh] overflow-y-auto">
              {/* Result Visibility Section */}
              <div className="p-6 border-b">
                <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Eye size={16} className="text-blue-600" />
                  Result Visibility
                </h4>
                <p className="text-xs text-gray-500 mb-4">Control what candidates see after completing their assessment</p>
                
                <div className="space-y-3">
                  {/* Show Results to Candidates */}
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                        <BarChart3 size={18} className="text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Show Score to Candidates</p>
                        <p className="text-xs text-gray-500">Display score, percentage and pass/fail status immediately after submission</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resultSettings.show_results_to_candidates}
                        onChange={(e) => setResultSettings(prev => ({ ...prev, show_results_to_candidates: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-6 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm"></div>
                    </label>
                  </div>
                  
                  {/* Show Correct Answers */}
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-green-100 rounded-lg flex items-center justify-center">
                        <CheckCircle size={18} className="text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Show Correct Answers</p>
                        <p className="text-xs text-gray-500">Reveal correct answers after submission (useful for learning)</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resultSettings.show_correct_answers}
                        onChange={(e) => setResultSettings(prev => ({ ...prev, show_correct_answers: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-gray-200 rounded-full peer peer-checked:bg-green-600 peer-checked:after:translate-x-6 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm"></div>
                    </label>
                  </div>
                  
                  {/* Show Leaderboard */}
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-100">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-amber-100 rounded-lg flex items-center justify-center">
                        <Trophy size={18} className="text-amber-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">Show Leaderboard</p>
                        <p className="text-xs text-gray-500">Allow candidates to view rankings and compare with others</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resultSettings.show_leaderboard}
                        onChange={(e) => setResultSettings(prev => ({ ...prev, show_leaderboard: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-gray-200 rounded-full peer peer-checked:bg-amber-600 peer-checked:after:translate-x-6 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all after:shadow-sm"></div>
                    </label>
                  </div>
                </div>
                
                {/* Preview Box */}
                {!resultSettings.show_results_to_candidates && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-xl border border-gray-200">
                    <p className="text-xs text-gray-600 flex items-center gap-2">
                      <Info size={14} />
                      Candidates will see: "Your assessment has been submitted. Results will be announced by the organizer."
                    </p>
                  </div>
                )}
              </div>
              
              {/* Proctoring Section */}
              <div className="p-6">
                <h4 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield size={16} className="text-purple-600" />
                  Proctoring Settings
                </h4>
                <p className="text-xs text-gray-500 mb-4">Configure anti-cheating measures during assessment</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {/* Fullscreen Enforcement */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Maximize size={16} className="text-purple-600" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">Fullscreen Mode</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={proctoringSettings.fullscreen_enforcement}
                        onChange={(e) => setProctoringSettings(prev => ({ ...prev, fullscreen_enforcement: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                    </label>
                  </div>
                  
                  {/* Tab Switch Detection */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Monitor size={16} className="text-purple-600" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">Tab Switch Detection</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={proctoringSettings.tab_switch_detection}
                        onChange={(e) => setProctoringSettings(prev => ({ ...prev, tab_switch_detection: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                    </label>
                  </div>
                  
                  {/* Copy/Paste Restriction */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Clipboard size={16} className="text-purple-600" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">Block Copy/Paste</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={proctoringSettings.copy_paste_restriction}
                        onChange={(e) => setProctoringSettings(prev => ({ ...prev, copy_paste_restriction: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                    </label>
                  </div>
                  
                  {/* Blur Detection */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <Eye size={16} className="text-purple-600" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">Blur Detection</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={proctoringSettings.blur_detection}
                        onChange={(e) => setProctoringSettings(prev => ({ ...prev, blur_detection: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                    </label>
                  </div>
                  
                  {/* Auto Warnings */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={16} className="text-purple-600" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">Auto Warnings</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={proctoringSettings.auto_warnings}
                        onChange={(e) => setProctoringSettings(prev => ({ ...prev, auto_warnings: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-purple-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                    </label>
                  </div>
                  
                  {/* Webcam Proctoring */}
                  <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl border border-amber-200">
                    <div className="flex items-center gap-2">
                      <Camera size={16} className="text-amber-600" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">Webcam Proctoring</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={proctoringSettings.webcam_proctoring}
                        onChange={(e) => setProctoringSettings(prev => ({ ...prev, webcam_proctoring: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-amber-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                    </label>
                  </div>
                </div>
                
                {/* Advanced Settings */}
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="p-3 bg-gray-50 rounded-xl">
                    <label className="text-xs font-medium text-gray-700 mb-1 block">Max Violations</label>
                    <input
                      type="number"
                      value={proctoringSettings.max_violations}
                      onChange={(e) => setProctoringSettings(prev => ({ ...prev, max_violations: parseInt(e.target.value) || 10 }))}
                      min="1"
                      max="50"
                      className="w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl border border-red-200">
                    <div className="flex items-center gap-2">
                      <AlertCircle size={16} className="text-red-600" />
                      <div>
                        <p className="text-xs font-medium text-gray-900">Auto Submit</p>
                        <p className="text-xs text-gray-500">On max violations</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={proctoringSettings.auto_submit_on_violation}
                        onChange={(e) => setProctoringSettings(prev => ({ ...prev, auto_submit_on_violation: e.target.checked }))}
                        className="sr-only peer"
                      />
                      <div className="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-red-600 peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all"></div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowSettingsModal(false)}
                className="px-5 py-2.5 text-gray-600 hover:bg-gray-200 rounded-xl font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={saveProctoringSettings}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-indigo-700 transition-all shadow-lg shadow-purple-200 flex items-center gap-2"
              >
                <CheckCircle size={18} /> Save Settings
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Plan Upgrade Modal */}
      {showUpgradeModal && upgradeInfo && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Zap size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Upgrade Your Plan</h3>
                  <p className="text-amber-100 text-sm">Unlock more rounds and features</p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-6">
              <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Round Limit Reached</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Your current <strong>{upgradeInfo.current_plan}</strong> plan allows maximum {upgradeInfo.max_rounds} round(s).
                      You currently have {upgradeInfo.current_rounds} round(s).
                    </p>
                  </div>
                </div>
              </div>
              
              <h4 className="font-semibold text-gray-800 mb-4">Choose a Plan to Upgrade</h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {upgradeInfo.upgrade_options?.map((plan) => (
                  <div 
                    key={plan.plan_id}
                    className={`border-2 rounded-xl p-5 transition-all hover:shadow-lg cursor-pointer ${
                      plan.plan_id === 'enterprise' 
                        ? 'border-purple-300 bg-gradient-to-br from-purple-50 to-indigo-50' 
                        : 'border-gray-200 hover:border-amber-300'
                    }`}
                    onClick={() => !upgrading && handleUpgradePlan(plan.plan_id)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h5 className="font-bold text-lg text-gray-800">{plan.name}</h5>
                      {plan.plan_id === 'enterprise' && (
                        <span className="text-xs px-2 py-1 bg-purple-600 text-white rounded-full">Popular</span>
                      )}
                    </div>
                    
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-gray-900">₹{(plan.price / 100).toLocaleString()}</span>
                      <span className="text-gray-500 text-sm">/challenge</span>
                      
                      {/* Proration Preview */}
                      {prorationPreview[plan.plan_id]?.pricing && (
                        <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-1 text-green-700 text-sm font-medium">
                            <CheckCircle size={14} />
                            <span>Proration Applied</span>
                          </div>
                          <div className="mt-2 space-y-1">
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">New plan price:</span>
                              <span className="text-gray-900">{prorationPreview[plan.plan_id].pricing.original_formatted}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Credit from current plan:</span>
                              <span className="text-green-600">-₹{(prorationPreview[plan.plan_id].pricing.proration_credit / 100).toLocaleString()}</span>
                            </div>
                            <div className="border-t border-green-200 my-1"></div>
                            <div className="flex justify-between font-semibold">
                              <span className="text-gray-800">You Pay:</span>
                              <span className="text-green-700 text-lg">{prorationPreview[plan.plan_id].pricing.final_formatted}</span>
                            </div>
                            {prorationPreview[plan.plan_id].pricing.savings_percentage > 0 && (
                              <p className="text-xs text-green-600 text-center mt-1">
                                Save {prorationPreview[plan.plan_id].pricing.savings_percentage}% with proration!
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      {loadingProration && !prorationPreview[plan.plan_id] && (
                        <div className="mt-2 flex items-center gap-2 text-sm text-gray-500">
                          <Loader2 size={14} className="animate-spin" />
                          Calculating your price...
                        </div>
                      )}
                    </div>
                    
                    <ul className="space-y-2 mb-4">
                      <li className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle size={16} className="text-green-500" />
                        {plan.max_rounds === -1 ? 'Unlimited rounds' : `Up to ${plan.max_rounds} rounds`}
                      </li>
                      {plan.plan_id === 'enterprise' && (
                        <>
                          <li className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle size={16} className="text-green-500" />
                            Unlimited participants
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle size={16} className="text-green-500" />
                            Advanced AI proctoring
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle size={16} className="text-green-500" />
                            Dedicated support
                          </li>
                        </>
                      )}
                      {plan.plan_id === 'pro' && (
                        <>
                          <li className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle size={16} className="text-green-500" />
                            Up to 10,000 participants
                          </li>
                          <li className="flex items-center gap-2 text-sm text-gray-600">
                            <CheckCircle size={16} className="text-green-500" />
                            Basic proctoring
                          </li>
                        </>
                      )}
                    </ul>
                    
                    <button
                      onClick={(e) => { e.stopPropagation(); handleUpgradePlan(plan.plan_id); }}
                      disabled={upgrading}
                      className={`w-full py-2.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                        plan.plan_id === 'enterprise'
                          ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white hover:from-purple-700 hover:to-indigo-700'
                          : 'bg-amber-500 text-white hover:bg-amber-600'
                      } disabled:opacity-50`}
                    >
                      {upgrading ? (
                        <><Loader2 size={16} className="animate-spin" /> Processing...</>
                      ) : (
                        <><Zap size={16} /> Upgrade Now</>
                      )}
                    </button>
                  </div>
                ))}
              </div>
              
              <p className="text-xs text-gray-500 text-center mt-4">
                Secure payment powered by Cashfree. Contact support for custom enterprise pricing.
              </p>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 border-t flex justify-end">
              <button
                onClick={() => { setShowUpgradeModal(false); setUpgradeInfo(null); }}
                className="px-5 py-2.5 text-gray-600 hover:bg-gray-200 rounded-xl font-medium transition-colors"
              >
                Maybe Later
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyEnterpriseDetailPage;
